import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
// U11 (E1) — scoped store subscriptions are shallow-compared; see the perf
// note above `draftEntries`.
import { useShallow } from 'zustand/react/shallow';
import {
  ArrowRight,
  ArrowsClockwise,
  Eye,
  EyeSlash,
  Lightning,
  MagicWand,
  Users,
  Warning,
} from '@phosphor-icons/react';
import { Alert } from '@clay-run/glaze/alert';
import { Badge, BadgeIcon } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { Icon } from '@clay-run/glaze/icon';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../../_shared/hooks';
import type { Campaign, CampaignMember } from '../../../_shared/launcher-campaign.types';
import {
  CHANNELS,
  CLAY_OWNED_TOKEN_CAPTION,
  type TemplateSegment,
} from '../../../_shared/launcher-copy-tokens';
import { useLauncherCampaign } from '../../launcher-api';
import { useLauncherMembers } from '../../launcher-api';
import { getLibrary, getPlaybook, getPlays, getSegmentation, getSequence, getSequenceUsage } from '../../api-client';
import { GENERATE_AI_BUTTON_WRAPPER_CLASS } from '../../ai-accent';
import { isVersionConflictError } from '../../conflict-errors';
import { CAMPAIGN_MOVED_DETAIL, generateTriggerState, generationCampaignMismatch } from '../../generate-with-ai-logic';
import {
  previewStepCopySegments,
  rosterMembersForGapCheck,
  rosterTokenGaps,
  selectSampleMember,
  stepTokenGaps,
} from '../../copy-token-flags';
import { duplicateSequenceWithEditedSteps } from '../../duplicate-sequence-copy';
import { gongFlowDisplayName } from '../../gong-flow-name';
import {
  adoptPlayLadderMoves,
  adoptUpdatePlaysResult,
  ensurePlayOwned,
  gateInPlayWrite,
} from '../../ensure-play-owned';
import { wasLadderMoveAdopted } from '../../ladder-adoption-signal';
import { isArchivedInLibrary } from '../../play-ownership';
import {
  fetchGongFlowSteps,
  fetchGongFlowsBrowse,
  gongStepBodyFormat,
  gongStepsToSequenceSteps,
  gongStepTypeLabel,
  importGongFlowSteps,
  isReplyAtOrdinal,
  sortStepsByPosition,
  stepOrdinal,
} from '../../gong-flow-picker-api';
import { matchedIdsForRungScope } from '../../members-panel-scope';
import { clearRungScope, setRungScope } from '../../rung-scope-store';
import {
  selectPreviewMemberId,
  selectSequenceEditorMode,
  setSequenceEditorMode,
  useSequencePreviewStore,
} from '../../sequence-preview-store';
import {
  isSharedMappingConflictError,
  renameSequence,
  savePlaySequenceCopy,
  saveSequenceSteps,
  updatePlays,
} from '../../mutations';
import {
  gongFlowsBrowseQueryKey,
  libraryQueryKey,
  playbookDetailQueryKey,
  playsQueryKey,
  segmentationDetailQueryKey,
  sequenceDetailQueryKey,
  sequenceUsageQueryKey,
} from '../../query-keys';
import { sequenceTokenFlags, tokenFlagBadgeText } from '../../sequence-token-flags';
import {
  rungDraftKey,
  seedDraftFromHead,
  type DraftRung,
} from '../../playbook-draft';
import { applyLadderSaveToCache } from '../../playbook-query-cache';
import { isInteractiveRowTarget } from '../../row-click-guard';
import { selectRung } from '../../rung-selection';
import {
  getSequenceCardMenuItems,
  renameWarningText,
  SAVE_GROUP_FIRST,
  SEQUENCE_UNAVAILABLE_NAME,
  sequenceSidebarTarget,
} from '../../sequence-card-menu';
// U9 (SN1) — `senderLabel` moved into `sender-choice.ts` so the rung header
// and Config's own sequence row share one label map (see its doc there).
// R5-4 — `effectiveSender` is the single read for what a rung actually sends
// as: the sequence's live `default_sender` whenever it has loaded, the
// rung's stale copy only during the loading gap. See that module's doc.
import { effectiveSender, senderLabel } from '../../sender-choice';
import {
  draftRungMapsToSequence,
  ensurePlaybookDraft,
  remapPlaybookDraftRungSequence,
  selectPlaybookDraft,
  setPlaybookDraftRungMapping,
  usePlaybookDraftStore,
} from '../../playbook-draft-store';
import {
  clearGeneratedCopy,
  decideStagedStep,
  editStagedStep,
  selectGeneratedCopyEntry,
  useGeneratedCopyStore,
} from '../../generated-copy-store';
import {
  closeGenerateSession,
  openGenerateSession,
  selectIsSessionOpenFor,
  useGenerateWithAIStore,
} from '../../generate-with-ai-store';
import {
  acceptGeneratedCopyDraft,
  clearCopyDraftIfUnchanged,
  clearCopyDrafts,
  copyDraftKey,
  mergeDraftsIntoSteps,
  resolveCopyDraftValues,
  selectCopyDraftEntriesFor,
  sequenceHasCopyDrafts,
  useSequenceCopyDraftStore,
} from '../../sequence-copy-draft-store';
import {
  applySettingsToCache,
  executeSettingsUpdateAll,
  forkSequenceWithSettings,
} from '../../sender-decision';
import type {
  LauncherChannel,
  PlaybooksGenerationInputs,
  PlaybooksLibraryResponse,
  PlaybooksSaveSequenceStepsResponse,
  PlaybooksSequenceDetailResponse,
  PlaybooksUpdatePlaysRequest,
  PlaybooksUsagePlace,
  PlaybooksUsageResponse,
  SequenceMapping,
  SequenceStep,
} from '../../schemas';
import { useGongReplyStepOrders } from '../../use-gong-reply-step-orders';
import { useMatchedMembersLadder } from '../../use-matched-members';
import { notReadyBadgeText } from '../../sequence-readiness';
import { useRungReadiness } from '../../use-rung-readiness';
import { isUsageSidebarOpened, UsageSidebarOpenedError } from '../../usage-sidebar-errors';
// QA round 8, item 6 — "Add first step" seeds a legacy zero-step sequence
// the same way `POST /sequences` seeds a brand-new one.
import {
  GONG_BLANK_STEP_TEXT,
  isContentlessGongStep,
  isGongEmailStep,
  REPLY_STEP_NO_SUBJECT_TEXT,
  seedStepsForChannel,
} from '../../step-content-rules';
import { confirmedFanOutSet, fallbackRetargetedPlace, originBelongsToFlow, pluralize } from '../../usage-sidebar-logic';
import { resumeReviewFromPending } from '../../usage-sidebar-resume';
import { flowContextKey } from '../../play-flow-context';
import { shouldClearFlowStores, useFlowSessionStore } from '../../flow-session-store';
import {
  consumeStepsReopenSignal,
  markUsageSidebarOriginMissing,
  openUsageSidebar,
  openUsageSidebarFromUsage,
  originKeyOf,
  retargetUsageSidebarPending,
  selectStepsReopenSignal,
  selectUsageSidebar,
  signalStepsReopen,
  useUsageSidebarStore,
  type PendingUsageSidebar,
  type UsageSidebarOrigin,
} from '../../usage-sidebar-store';
import { settingsDraftForReopen } from '../../settings-draft-restore';
import { distinctUnsentPlays, GROUP_MOVED_SENTENCE, usedInPlacesText } from '../../usage-gate';
import {
  useUsageSidebarDecision,
  type UsageSidebarDecisionHandlers,
} from '../../use-usage-sidebar-decision';
import { GenerateWithAIDrawer } from '../GenerateWithAIDrawer';
import { KebabMenu } from '../KebabMenu';
import { NameDrawerField } from '../NameDrawerField';
import { PushDrawer } from '../PushDrawer';
import { SanitizedHtml, escapeHtml } from '../SanitizedHtml';
import { SequenceFlowIdPopover } from '../SequenceFlowIdPopover';
import { SequenceInflectionPopover } from '../SequenceInflectionPopover';
import { saveSequenceSettingsWithGate, SequenceSenderPopover } from '../SequenceSenderPopover';
import { SequenceStepEditor } from '../SequenceStepEditor';
import { SequenceMappingDrawer } from './SequenceMappingDrawer';

/**
 * Launcher V2 — the Sequences step (plan's `renderStep2`, v6 prototype
 * ~line 6270): a left rail of segmentation cards (minus all version chrome —
 * no branch/divergence dots, no "N pinned behind" language) and, per the
 * selected rung, a per-channel sequence-editor shell. Steps render EXPANDED
 * by default (locked decision) via Glaze `CollapsibleCard`; editable fields
 * are `useDraftState` drafts saved through `mutations.saveSequenceSteps` —
 * REAL as of PR C / U4B (AI copy generation is still PR C / U7). Decision 13:
 * an unmapped rung gets the plain "No sequence mapped" note, never a
 * suppression badge/vocabulary.
 *
 * ── DISCOVERY (same gap `QualifyStep` documents): no `GET /v2/plays/:id`,
 * so the current Play is re-derived from the campaign-scoped `getPlays` list
 * filtered to `playId`. Both steps' `useQuery` calls for `library`/`plays`/
 * `playbook` share query keys, so React Query dedupes them — mounting this
 * step does not re-fetch what Qualify already loaded.
 *
 * ── GONG FLOW BINDING (PR C / U4B, Key Decision 5; restructured by Live-QA
 * round 2 Batch 4, C1/C2) — a `gong_engage` rung's editor shell no longer
 * hosts a picker of its own at all. `sequenceSummary.flowId` — the real,
 * server-bound fact, always read from the library/sequence-detail queries,
 * never from local component state — drives one of three thin card states:
 *
 *  - Unbound (`flowId` null): a prompt + "Choose a Gong flow" button.
 *  - Bound: a plain "Gong flow: {name}" line (resolved from the Gong browse
 *    catalog, best-effort, falling back to the raw id) with a "Change flow"
 *    button beside it.
 *  - Bound but the sequence has NO STEPS (import failed, or a pre-existing
 *    flow-bound sequence never got them): a "Load steps from Gong" retry
 *    button — nothing to lose, so no confirm.
 *
 * Both "Choose a Gong flow" and "Change flow" open the EXISTING mapping
 * drawer (`setIsMappingModalOpen`), seeded onto its Gong tab via
 * `initialChannelFilter="gong_engage"` — the standalone `GongFlowPicker` +
 * `PushDrawer` + lock-confirm `AlertDialog` this shell used to mount directly
 * are gone outright (`handlePickFlow`/`handleConfirmFlow` deleted). The
 * picker's only host is `SequenceMappingDrawer`'s Gong tab (Unit D2, Batch 6)
 * — both buttons land the rep on a "Browse Gong flows" button there that
 * opens `LauncherGongFlowBrowse` (Flow's real panel, round-3b QA fixes G3),
 * which dispatches a click three ways (create/reuse, first-bind, or a D4
 * confirm-rebind) rather than this card doing any of that itself. The retry button
 * here still calls `gong-flow-picker-api.ts`'s `importGongFlowSteps` directly
 * (C1's fetch → bind → save orchestration) — it needs no flow PICK at all
 * (the flow is already known), so it is unaffected by D2's dispatch.
 *
 * 2026-09-02 rip-out (U3, folding in the old plan's Round-2 B1): the bound
 * line used to read "Locked · {name}" — that wording implied the same kind
 * of pin/lock that rip-out removed everywhere else, which was never quite
 * accurate even before Batch 6: same-flow re-confirms were always a no-op,
 * and a genuine swap onto a DIFFERENT flow is now a real, supported rebind
 * (D4, Batch 6 — repealed the one-way lock this comment used to describe),
 * reachable through the "Change flow" button right here.
 *
 * ── RAIL RENDERS THE STAGED LADDER, AND SEQUENCES ARE ASSIGNABLE HERE
 * (Codex adversarial-review fix, PR C) — this step used to render
 * `playbookQuery.data.head.rungs`, so a rung the rep had just added on Qualify
 * (no server id yet) was invisible, and a rung they had staged for REMOVAL was
 * still listed. It now renders `playbook-draft-store`'s ladder, and each rung's
 * `SequenceEditorShell` carries a `SequenceMappingDrawer` entry point ("Choose
 * sequence" when unmapped, "Change sequence" when mapped). Before this,
 * `setPlaybookDraftRungMapping` had no production caller anywhere: there was no
 * path through the shipped UI to give a new rung a sequence, or to change an
 * existing rung's. Mapping edits are WRITE-THROUGH like every other rung edit
 * (2026-09-02 rip-out, U4) — `setPlaybookDraftRungMapping` persists
 * immediately via `playbook-draft-store.ts#persistLadder`, not on a later
 * Review-step save.
 *
 * ── EVERY RUNG ALWAYS RENDERS ITS SEQUENCE'S HEAD, AND EDITING IS ALWAYS
 * AVAILABLE (2026-09-02 rip-out, U3) — there used to be a version-pinning
 * concept here: a rung could render a historical "locked at send" snapshot
 * (read-only, no Save/Discard/Generate) or an in-play "branch" row isolating
 * one campaign's copy edits from every other rung/play sharing the same
 * Sequence. Both are gone. `renderedVersion` is now simply
 * `sequenceQuery.data?.head`, unconditionally, for every rung. Editing copy
 * from ANY rung is a plain main-line save (`handleSaveStep`,
 * `POST /v2/sequences/:id/versions`) that lands on the Sequence's shared head
 * — visible to every other rung/play mapped to it. Diverging on purpose is an
 * explicit "Duplicate this sequence" action (`SequenceMappingDrawer`'s
 * "Create a new Sequence" doorway, or `duplicateSequence`), never an implicit
 * side effect of which rung a rep happened to edit from. See
 * plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md for the full
 * rationale; `sequence-copy-draft-store.ts`'s per-rung draft SCOPING (below,
 * "D1 shared-edit-leak fix") is unrelated and unaffected — two rungs mapping
 * the same Sequence still never see each other's UNSAVED typing, only each
 * other's SAVED copy, which was always the point of a shared Sequence.
 *
 * ── SAVE-VS-SAVE-AS-NEW-COPY GATE (round-2-followup plan, Unit A1) ────────
 * The rip-out above deliberately made every rung's Save land on the shared
 * head with no isolation — the "Create a new Sequence"/`duplicateSequence`
 * doorway was the intended explicit-divergence escape hatch, but nothing
 * actually offered it AT the moment a rep was about to overwrite a Sequence
 * more than one rung/Play maps. `handleSaveStep`'s `saveWithGate` closes that
 * gap: it reads `sequenceQuery.data.activeMappingCount` (computed
 * server-side, `schemas.ts:439-457`) before writing. At `1` (sole use,
 * unaffected) it's still exactly the main-line save described above, no
 * dialog. Above `1` it offers "Save" (this same main-line write — every
 * mapping sees it, matching this section's original design) or "Save as new
 * copy" (`handleSaveAsNewCopy` — duplicates the Sequence via
 * `duplicateSequenceWithEditedSteps`, `../../duplicate-sequence-copy.ts`,
 * writes the edit onto the duplicate only, then remaps ONLY this rung onto
 * it via `remapPlaybookDraftRungSequence`). This is the explicit-divergence
 * path the section above already named — it just wasn't reachable from the
 * Save button itself until this unit.
 *
 * ── round-2-followup-codex-fixes plan, Unit B4 (historical — superseded by
 * U2a below) ────────────────────────────────────────────────────────────────
 * Three fixes on top of the above, back when the gate was an `openAlertDialog`
 * whose underlying Promise settled on the rep's button click: (1) the dialog
 * settled on EVERY dismiss path (Escape, the header's X, a rapid second click
 * replacing the still-open dialog); (2) "Save" passed the `activeMappingCount`
 * it displayed as `confirmedMappingCount`, so the server could refuse a save
 * whose warning was now stale (`shared_mapping_conflict`); (3)
 * `handleSaveAsNewCopy` remapped via `remapDraftRungSequence`'s write-through
 * wrapper instead of spreading a render-time-captured `mapping` snapshot, so a
 * 409 retry re-read the rung's fresh `sender`/`cc`/`replyTo` rather than
 * clobbering a concurrent edit to them (finding 3) — see that function's own
 * doc comment in `playbook-draft.ts`. (2) and (3) are UNCHANGED by U2a; (1)'s
 * whole Promise-settling mechanism is gone (see `saveWithGate`'s own doc) —
 * the sidebar is opened, never awaited, so there is nothing left to settle.
 *
 * ── THE MEMBERS TABLE DRIVES THE PREVIEW, AND THERE IS NO DRAWER (C3, plan
 * `2026-09-01-001`, decisions #4/#5/#6) — the shared `MembersPanel` slot moved
 * from the left rail into the MAIN column, directly above the sequence editor
 * whose copy output it drives. Three things follow, and all three replace
 * something that used to be a drawer or component state:
 *
 *   • `RungRosterDrawer.tsx` is DELETED. Its two jobs (the
 *     unresolved-merge-fields drill-down and the "Preview with N" member
 *     picker) are the members table's "Missing fields" column and its
 *     clickable rows respectively. Nothing here opens a panel to list members.
 *   • `editorMode`/`selectedMemberId` live in `sequence-preview-store.ts`, not
 *     in `SequenceEditorShell` state, because the click that picks a member now
 *     comes from that sibling table.
 *   • Entering Preview (via the toggle, the "Preview with N" button, or a rail
 *     click while previewing) SETS THE RUNG SCOPE, so the table filters to the
 *     rung's real first-match-wins membership. Until C3 only Qualify's "Preview
 *     matches" ever set that scope, which is why this step's table always
 *     showed the whole roster.
 */

const CHANNEL_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  CHANNELS.map((c) => [c.value, c.label]),
);

function channelLabel(channel: string): string {
  return CHANNEL_LABEL_BY_VALUE[channel] ?? channel;
}


/** U11 — the display name for the "Previewing {name}" badge and `SamplePreviewCard`'s own line, same fallback chain `MembersPanel` uses for its own rows. */
function previewMemberLabel(member: CampaignMember): string {
  return member.contact_name || member.contact_email || member.id;
}

// ─────────────────────────────────────────────────────────────────────────────
// Left rail — one card per rung, selecting scopes the editor workspace.
// ─────────────────────────────────────────────────────────────────────────────

interface SegmentationRailCardProps {
  /**
   * The rung's staged-or-persisted mapping (PR C, U5) — NOT necessarily
   * `rung.mapping`: the caller overlays any sender edit staged in the
   * playbook draft (see this file's render loop), so this card and the
   * selected rung's editor shell can never disagree about the same rung's
   * sender.
   */
  mapping: SequenceMapping | null;
  index: number;
  segmentationName: string;
  /** `null` when the library row is absent — rendered as "Sequence unavailable", never the id ([R20]). */
  sequenceName: string | null;
  /** [R18] The rung's sequence is a Library piece archived while this play still uses it. */
  sequenceArchived: boolean;
  /**
   * `true` for a rung whose "Add segmentation" write-through save has not yet
   * resolved (PR C hardening; brief window only as of the 2026-09-02
   * write-through rip-out, U4) — it is routable here (that is the fix), but
   * the rep needs to know it has no server id yet. Same vocabulary
   * `QualifyStep`'s `PendingRungRow` already uses for the same rung.
   */
  isUnsaved: boolean;
  library: PlaybooksLibraryResponse | undefined;
  isSelected: boolean;
  onSelect: () => void;
  /**
   * D1 shared-edit-leak fix — this rung's own `draftKey`, so the "Unsaved
   * changes" badge below reads ONLY this rung's drafts. See
   * {@link sequenceHasCopyDrafts}'s doc: before this, the badge was keyed by
   * sequence alone, so two rungs mapped to the same sequence showed each
   * other's unsaved typing.
   */
  draftScope: string;
}

function SegmentationRailCard({
  mapping,
  index,
  segmentationName,
  sequenceName,
  sequenceArchived,
  isUnsaved,
  library,
  isSelected,
  onSelect,
  draftScope,
}: SegmentationRailCardProps) {
  // R6 — v1-parity Ready badge (PR C, U5). H3 — `reasons` drives the
  // "Not ready — why" copy below.
  const { readiness, reasons } = useRungReadiness(mapping, library);
  /**
   * D3 (U9, item 18) — does any step of THIS rung's sequence carry unsaved
   * typing? The marker has to live HERE and not only on the step editor: the
   * rung a rep switched away from is the one whose draft used to disappear,
   * and its card is the only thing still on screen representing it.
   *
   * D1 shared-edit-leak fix — scoped by `draftScope` (this rung's own
   * `draftKey`) as well as sequence. This USED to be keyed by SEQUENCE alone
   * — the drafts were addressed `${sequenceId}:${position}`, which is why two
   * rungs mapped to the same sequence both showed the marker for either
   * rung's typing, even though only one of them had actually been typed into.
   * A save still advances the shared Sequence head for every rung mapping it
   * (2026-09-02 rip-out — see `SequencesStep`'s module doc), but this rung's
   * own UNSAVED draft is not: two rungs sharing a sequence still never see
   * each other's in-progress typing, only each other's saved copy.
   */
  const hasCopyDrafts = useSequenceCopyDraftStore((state) =>
    sequenceHasCopyDrafts(state, draftScope, mapping?.sequenceId),
  );

  /**
   * U8 (plan Key Decision 10, R7) — this rung's authoring flags, live: the
   * mapped sequence's saved head overlaid with THIS rung's own unsaved
   * drafts (same `liveSteps` pattern `SequenceEditorShell` below uses for
   * its roster gap check), so the badge shown here for the SELECTED rung
   * matches the step editor exactly, and an UNSELECTED rung's card still
   * shows its own sequence's flags without waiting for a rail click.
   *
   * Same query key (`sequenceDetailQueryKey(id)`) as `SequenceEditorShell`'s
   * `sequenceQuery` — React Query dedupes by key, so selecting this rung
   * never triggers a second fetch; this only adds a fetch for a rung that
   * would not otherwise have one (i.e. any mapped rung that isn't currently
   * selected), which is the point: the flag has to be visible "on the
   * sequence/step ... until fixed", not gated on which rung happens to be
   * selected.
   */
  const { apiFetch } = useApi();
  const flagSequenceQuery = useQuery({
    queryKey: sequenceDetailQueryKey(mapping?.sequenceId ?? ''),
    queryFn: () => getSequence(apiFetch, mapping!.sequenceId),
    enabled: !!mapping,
  });
  const flagDraftEntries = useSequenceCopyDraftStore(
    useShallow(selectCopyDraftEntriesFor(draftScope, mapping?.sequenceId ?? '')),
  );
  const tokenFlagBadge = useMemo(() => {
    if (!mapping) return null;
    const savedSteps = flagSequenceQuery.data?.head?.steps;
    if (!savedSteps) return null;
    const liveSteps = savedSteps.map((s) => {
      const live = resolveCopyDraftValues(
        flagDraftEntries[copyDraftKey(draftScope, mapping.sequenceId, s.position)],
        { subject: s.subject ?? '', body: s.body ?? '' },
      );
      return { position: s.position, subject: live.subject, body: live.body };
    });
    return tokenFlagBadgeText(sequenceTokenFlags(liveSteps));
  }, [mapping, flagSequenceQuery.data, flagDraftEntries, draftScope]);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-1 rounded-lg border p-3 text-left ${isSelected ? 'border-border-action bg-bg-blue-light' : 'border-border-primary bg-bg-primary'}`}
    >
      <div className="flex items-start gap-2">
        <Text size="xs" variant="tertiary" className="w-4 shrink-0 text-right tabular-nums">
          {index + 1}
        </Text>
        <Text size="xs" weight="medium" className="min-w-0 flex-1 wrap-break-word">
          {segmentationName}
        </Text>
        {isUnsaved && <StatusBadge status="neutral">Not saved yet</StatusBadge>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-6">
        {mapping ? (
          <>
            <Badge>{channelLabel(mapping.channel)}</Badge>
            <Text size="xs" variant="tertiary" className="truncate">
              {sequenceName ?? SEQUENCE_UNAVAILABLE_NAME}
            </Text>
            {sequenceArchived && <StatusBadge status="neutral">Archived in Library</StatusBadge>}
            {readiness === 'ready' && <StatusBadge status="success">Ready</StatusBadge>}
            {readiness === 'not_ready' && (
              <StatusBadge status="warning">{notReadyBadgeText(reasons, mapping.channel)}</StatusBadge>
            )}
            {tokenFlagBadge && <StatusBadge status="warning">{tokenFlagBadge}</StatusBadge>}
            {hasCopyDrafts && <StatusBadge status="warning">Unsaved changes</StatusBadge>}
          </>
        ) : (
          <Text size="xs" variant="tertiary">
            No sequence mapped — members here don&apos;t get a send.
          </Text>
        )}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-step editor — extracted to `../SequenceStepEditor.tsx` (QA-fixes E1 step 1):
// its props/draft-store have zero rung/play/mapping coupling, so the Config page's
// `SequenceCopyPane` can reuse it directly. `SequenceEditorShell` below (the rung
// rail/mapping/Gong-flow wrapper) does NOT extract cleanly and stays here.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Sequence-editor shell for the selected, mapped rung.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Playbooks QA round 4, U9 (SN1) / Decision 3 — the rung-level
 * `InlineSenderPopover` that used to live here is GONE, along with the
 * `setPlaybookDraftRungSender`/`setDraftRungSender` draft ops it was the only
 * caller of. There is still no PER-RUNG override anywhere in this shell —
 * that decision stands.
 *
 * Playbooks QA round 5, R5-2 — what changed since is WHERE the sequence's
 * own control is reachable from. Round 4 removed the rung-level popover and
 * put the ONLY sender control on the Config → Sequences row, reasoning (per
 * the round-4 doc) that this "supersedes the original L4 ask for a
 * primary-blue rung-level sender button" — but Spencer's L4 ask was for a
 * control reachable from the PLAY, not exclusively from Config, and the
 * popover writes the SEQUENCE either way, so hosting a second instance of it
 * in this card's header cluster (next to Generate with AI) is not a
 * per-rung override. R5-2 renders `SequenceSenderPopover` there,
 * `variant="primary"` per L4. `components/SequenceSenderPopover.tsx` still
 * owns the one write (`playbooks_sequence.default_sender`); this shell only
 * gained a second place to open the same control.
 *
 * R5-4 — the flow-line label below reads `effectiveSender(sequenceSummary,
 * mapping)` (`sender-choice.ts`), the sequence's LIVE value, not the rung's
 * stale `mapping.sender` copy — see that module's doc for why (round 4's
 * Decision 11, copy-on-map, was the bug this fixes).
 */

/**
 * C1 — the one cache-write shape shared by every path that lands a new
 * sequence head into the `sequenceDetailQueryKey(id)` query: a plain
 * hand-typed save (`SequenceEditorShell`'s `handleSaveStep`), the "Load steps
 * from Gong" retry import (`handleLoadStepsFromGong`), and — D3 — the
 * play-level `handleUsageSidebarUpdateAll` in `SequencesStep`, hoisted out of
 * `SequenceEditorShell` so it keeps working across a rung switch. Module
 * scope (not a `SequenceEditorShell`/`SequencesStep` closure) so both can
 * share it without either owning the other's `queryClient` reference.
 *
 * Play isolation (plan 2026-09-25-002, U5) — takes no `playId`: a play-owned
 * sequence is its own identity (Decision 12), so `response` — whether it
 * came from a play-copy save or a template write — always lands on the ONE
 * cache entry `sequenceDetailQueryKey(sequenceId)` names, and the NEXT save
 * from this editor sends that same head as `expectedVersionId` regardless of
 * which path wrote it.
 */
function applySequenceHeadToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  sequenceId: string,
  response: { versionId: string; versionNumber: number | null; steps: SequenceStep[] },
) {
  queryClient.setQueryData(
    sequenceDetailQueryKey(sequenceId),
    (old: PlaybooksSequenceDetailResponse | undefined) =>
      old
        ? {
            ...old,
            head: {
              versionId: response.versionId,
              versionNumber: response.versionNumber,
              steps: response.steps,
            },
          }
        : old,
  );
}

function SequenceEditorShell({
  playId,
  playbookId,
  rung,
  rungName,
  library,
  sampleMember,
  rosterMembers,
  campaignMembers,
  campaign,
  campaignSfdcId,
  onScopeToRung,
}: {
  /**
   * U2a — the Play this rung belongs to, threaded ONLY into the
   * `UsageSidebarOrigin` a shared save carries (`usage-sidebar-store.ts`'s
   * `'play'` origin needs it for the "back to editing" deep link) and into
   * `saveWithGate`, which routes onto the play
   * (`POST /plays/:id/sequences/:sid/copy`) instead of the template head.
   * Play isolation (plan 2026-09-25-002, U5) — does NOT scope this shell's
   * sequence read: a play's sequence is its own identity, so the read is the
   * same unscoped `GET /sequences/:id` the playbook doorway uses.
   *
   * terra-qyt.14 (U9) — `undefined` in playbook mode: the origin built below
   * becomes the `'playbook'` variant instead (no `playId`/`campaignSfdcId`,
   * `seedCampaignSfdcId` in their place — see `usage-sidebar-store.ts`'s own
   * doc for why the two origins are treated identically everywhere else).
   */
  playId?: string;
  /** The playbook this rung belongs to — threaded to `SequenceMappingDrawer`'s write-through save. */
  playbookId: string;
  /**
   * The rung as STAGED (PR C) — a `DraftRung`, not a `PlaybookRung`. Two things
   * follow from that and both are the point: `rung.mapping` already carries any
   * staged sequence/sender edit (so this shell can never disagree with the rail
   * card next to it), and `rung.draftKey` addresses a rung that has no server
   * `id` yet, which is how a rung the rep just added on Qualify becomes mappable
   * here at all (Codex adversarial-review fix — before this, the whole step
   * rendered `head.rungs`, so a staged rung was invisible and unmappable).
   */
  rung: DraftRung;
  /** The rung's segmentation name, resolved by the caller from the library. */
  rungName: string;
  library: PlaybooksLibraryResponse | undefined;
  /**
   * U6 (R3) — the sample contact for this shell's sample-preview card,
   * resolved once by `SequencesStep` (`selectSampleMember`) for the selected
   * rung. `null` when the campaign has no members loaded yet.
   */
  sampleMember: CampaignMember | null;
  /**
   * U10 (item 19, R13) — the roster this shell's token-gap check runs
   * against: the rung's matched members (real scope) or the whole campaign
   * roster (unsaved rung, no ladder entry yet) — resolved once by
   * `SequencesStep` (`rosterMembersForGapCheck`) so it's the same set
   * `MembersPanel` would show for this rung.
   */
  rosterMembers: readonly CampaignMember[];
  /**
   * C3 — the WHOLE campaign roster, for resolving a preview member the rep
   * clicked in the shared members table while it was unscoped (see
   * `previewMember` below). Never used for the gap check, which stays on
   * `rosterMembers`.
   */
  campaignMembers: readonly CampaignMember[];
  /**
   * U10 fix (roster token-gap false-positive) — the real campaign, fetched by
   * `SequencesStep` via `useLauncherCampaign(campaignSfdcId)` (same hook
   * `QualifyStep`'s `ContextCard` already uses). `null` while the campaign
   * query is still loading — the same transient state `QualifyStep` tolerates
   * — is treated exactly like "no campaign data yet", not a crash: campaign-
   * scoped tokens (`{{Event Name}}`/`{{Event Date}}`/`{{Campaign Type}}`)
   * simply resolve empty for that one instant, same as before this fix,
   * rather than the check throwing.
   */
  campaign: Campaign | null;
  /**
   * U12B (R16) — the campaign the generate-copy endpoint assembles its prompt
   * from (`POST /v2/sequences/:id/generate-copy`'s one required field). Threaded
   * as the raw SFDC id rather than read off the `campaign` object above so
   * Generate also works in the window where `useLauncherCampaign` is still
   * loading: the server reads the campaign row itself, so the id is all the
   * client legitimately needs to know.
   */
  campaignSfdcId: string;
  /**
   * C3 — scopes the shared `MembersPanel` to THIS rung (the caller owns the
   * `playbookId` and the rung's resolved name, and clears the scope for an
   * unsaved rung, which has no ladder entry to filter by).
   */
  onScopeToRung: () => void;
}) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  /**
   * U2a — this rung's identity as a "Where is this used" sidebar origin.
   * `rungId: null` for a rung staged this session with no server id yet —
   * see that type's own doc.
   *
   * terra-qyt.14 (U9) — the `'playbook'` variant when `playId` is undefined
   * (playbook mode): no `campaignSfdcId` to carry (a playbook can be
   * authored before a campaign is selected), `seedCampaignSfdcId` in its
   * place — `''` (no seed yet) becomes `null`, never the empty-string
   * sentinel, matching `flowContextFromRoute`'s own convention.
   */
  const origin: UsageSidebarOrigin =
    playId !== undefined
      ? {
          kind: 'play',
          playId,
          campaignSfdcId,
          playbookId,
          rungId: rung.id ?? null,
          rungDraftKey: rung.draftKey,
        }
      : {
          kind: 'playbook',
          playbookId,
          seedCampaignSfdcId: campaignSfdcId === '' ? null : campaignSfdcId,
          rungId: rung.id ?? null,
          rungDraftKey: rung.draftKey,
        };
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  /**
   * C2 — which channel the mapping drawer should land on when it opens.
   * `undefined` for the generic "Change sequence" entry point; `'gong_engage'`
   * for the two Gong-specific doorways ("Choose a Gong flow" / "Change
   * flow"), so both land directly on `SequenceMappingDrawer`'s Gong tab
   * (D2, Batch 6) — the `LauncherGongFlowBrowse` host (round-3b QA fixes
   * G3), not the plain sequence list.
   */
  const [mappingModalChannelFilter, setMappingModalChannelFilter] = useState<LauncherChannel | undefined>(
    undefined,
  );
  /** C1 — the "Load steps from Gong" retry affordance's in-flight state. */
  const [isImportingSteps, setIsImportingSteps] = useState(false);
  /** QA round 8, item 6 — the non-Gong empty-state "Add first step" doorway's in-flight state. */
  const [isAddingFirstStep, setIsAddingFirstStep] = useState(false);
  /**
   * U5 (round-3-QA-fixes plan) — "Remove sequence"'s in-flight guard. A
   * double-click on the same rung must not fire two write-through unmap
   * saves; disabling the button while this is true is the whole fix.
   */
  const [isRemovingMapping, setIsRemovingMapping] = useState(false);
  /**
   * U1 (R-L3, R-D4) — the kebab's Rename drawer state. Declared here, ABOVE
   * the `if (!mapping)` early return below, like every other hook in this
   * component — a hook declared AFTER that return would run on every render
   * while mapped and skip entirely once `handleRemoveMapping` lands and this
   * same component instance re-renders unmapped, which is exactly the
   * "Rendered fewer hooks than expected" crash the Rules of Hooks forbid
   * (caught by this unit's own `playbooks-sequence-card-menu.test.tsx`
   * "Remove sequence via the kebab" case).
   */
  const [renameOpen, setRenameOpen] = useState(false);
  const [savingRename, setSavingRename] = useState(false);
  /**
   * Plan 2026-09-29-001 U2 (rulebook R14, B5) — the rename this group has
   * sitting refused on a Save panel, or `null`. The drawer is remounted on
   * every open (`{renameOpen && …}`), and `NameDrawerField` seeds once per
   * mount, so this is read at open time: a rep who closes the drawer on a
   * refusal and reopens it sees the name they typed, not the saved one they
   * had already decided to change. Matched on the GROUP, never the piece —
   * see `settings-draft-restore.ts`.
   */
  const usageSidebarPending = useUsageSidebarStore((state) => state.pending);
  const pendingRenameDraft = settingsDraftForReopen(usageSidebarPending, origin, 'name');
  const pendingRenameName = pendingRenameDraft?.field === 'name' ? pendingRenameDraft.name : null;
  /**
   * Live-QA fixes, A2 — this card used to be an uncontrolled
   * `<CollapsibleCard>` (default open, no way for anything outside it to
   * toggle it). Made controlled so the whole-header click (below, on
   * `CollapsibleCardHeader`'s children) has state to flip, precedent at
   * `QualifyStep.tsx`'s `WaterfallCard` (`const [open, setOpen] =
   * useState(true)`).
   */
  const [isCardOpen, setIsCardOpen] = useState(true);
  /**
   * U12 (round-3 QA plan, Decision 12) — the "Steps" accordion's own local
   * open state, separate from `isCardOpen` above (the outer rung card) and
   * from each `SequenceStepEditor`'s own per-step open state.
   *
   * Round-4 QA plan U1 (R-L1, Decision 2) flips the default: live QA found
   * the previously-`true` default made every mapped rung open on a screen
   * full of step editors the rep hadn't asked to see. Now starts `false` —
   * and resets to `false` (not `true`) below whenever `mapping?.sequenceId`
   * changes, so swapping which sequence this rung maps to never leaves a
   * PREVIOUS mapping's OPEN choice stuck open either. Round-3 U12's original
   * intent (a rep's just-generated result must be visible immediately) is
   * preserved behaviorally instead of via the default: the `onApply` wrapper
   * passed to `GenerateWithAIDrawer` below calls `setStepsOpen(true)` once an
   * apply-path save actually lands.
   */
  const [stepsOpen, setStepsOpen] = useState(false);
  /**
   * U11 (item 20, R13, landmine 7/D8) — the sequence-level Edit | Preview
   * toggle (prototype `sequenceCard()`'s sub-header,
   * `playbooks-prototype-v6.html:6602-6610`), replacing the always-on
   * single-sample `SamplePreviewCard` this shell used to render unconditionally
   * below the step editors. Default `'edit'` matches the prototype's
   * documented default (`:2912`) — landmine 3.
   *
   * C3: both this and the previewed member moved OUT of component state into
   * `sequence-preview-store.ts`, because the click that picks a member now
   * comes from a row of the shared `MembersPanel` table — a sibling this shell
   * has no props path to. See that store's module doc.
   */
  const editorMode = useSequencePreviewStore(selectSequenceEditorMode);
  /**
   * The member the Preview mode interpolates for — `null` until the rep clicks
   * a row of the members table (prototype `previewMember(r)`, `:6434-6438`);
   * falls back to the shell's own `sampleMember` prop (U6's selection) so
   * Preview mode has something to show the first time it's toggled on, before
   * any explicit pick. Resolved against `rosterMembers` (D10 — the SAME roster
   * U10's gap check uses), not the whole campaign, so the "Preview with N"
   * count and the resolved member agree with each other.
   */
  const selectedMemberId = useSequencePreviewStore(selectPreviewMemberId);
  const mapping = rung.mapping;
  const sequenceSummary = mapping ? library?.sequences.find((s) => s.id === mapping.sequenceId) : undefined;
  /**
   * U12 / round-4 U1 — see `stepsOpen`'s own doc above: reset (to CLOSED, as
   * of U1) on every REP-PICKED sequence swap.
   *
   * (this bead) — NOT on copy-on-write silently repointing this same rung
   * from a Library identity onto this play's own fresh copy of it
   * (`ensure-play-owned.ts#adoptPlayLadderMoves`, fired by the in-play save gate's
   * first save, the rep's Reload after a stale refusal, or "Load steps from Gong"). That id
   * change is not a rep choice — closing the editor mid-edit here just makes
   * the rep re-open "Steps" to see the typing they never stopped doing. The
   * ref tracks the PREVIOUS `sequenceId` (not just whether this one changed)
   * because telling the two cases apart needs both ends of the transition:
   * `wasLadderMoveAdopted('sequence', previous, next)` matches only the EXACT
   * `(fromId, toId)` pair `adoptPlayLadderMoves` just signaled, so a genuine swap
   * onto a different sequence (any id pair the signal doesn't name) still
   * falls through to the reset below, same as today.
   */
  const previousSequenceIdRef = useRef(mapping?.sequenceId);
  useEffect(() => {
    const previousSequenceId = previousSequenceIdRef.current;
    previousSequenceIdRef.current = mapping?.sequenceId;
    if (previousSequenceId === mapping?.sequenceId) return;
    if (wasLadderMoveAdopted('sequence', previousSequenceId, mapping?.sequenceId)) return;
    setStepsOpen(false);
  }, [mapping?.sequenceId]);
  /**
   * Round-4 QA codex-review fix (batch 1, finding 2) — U1's auto-open-Steps
   * intent (see `stepsOpen`'s own doc above) only reaches THIS shell's own
   * `onApply` wrapper's `.then`, which never runs once `saveWithGate` gates
   * the save through the usage sidebar: the real write then happens in the
   * play-level `handleUsageSidebarUpdateAll`/`handleUsageSidebarSaveNew`,
   * which have no props path back into this per-rung shell's local state.
   * `usage-sidebar-store.ts#signalStepsReopen` closes that gap; this effect
   * is the shell-side half — it reopens Steps only when the signal names
   * THIS rung, and runs in the SAME commit as the `mapping?.sequenceId`
   * reset effect immediately above when both fire together (the save-new
   * path remaps this rung's `mapping.sequenceId` AND signals in the same
   * synchronous handler body, so React batches both effects into one commit
   * — declaration order below the reset effect is what makes this one win).
   */
  const stepsReopenSignal = useUsageSidebarStore(selectStepsReopenSignal);
  useEffect(() => {
    if (stepsReopenSignal !== null && stepsReopenSignal.rungDraftKey === rung.draftKey) {
      setStepsOpen(true);
      // Round-4 QA codex-review fix (wave 1, finding 6) — consume it. An
      // un-consumed signal is permanent, so every later remount of THIS
      // rung's shell (rail click away and back, a `?step=` round trip) read
      // the same stale signal and forced Steps open again. See
      // `consumeStepsReopenSignal`'s own doc for the epoch guard.
      consumeStepsReopenSignal(stepsReopenSignal.epoch);
    }
  }, [stepsReopenSignal, rung.draftKey]);
  /**
   * U12B (R16) — this sequence's AI-copy generation: in flight, or generated
   * and awaiting Accept/Discard. Read from `generated-copy-store.ts`, NOT local
   * state, for the same structural reason U9's drafts live in a store: a rail
   * click unmounts this whole shell (`key={selectedRung.draftKey}`), and a
   * generated draft is the most expensive thing on this screen to lose (plan
   * landmine 5). See that store's module doc.
   *
   * Scoped by THIS rung's own `draftKey` (Codex adversarial-review fix,
   * Issue 2, 2026-09-02) — a generation started on a different rung mapping
   * the same sequence must not surface here.
   */
  const generation = useGeneratedCopyStore(selectGeneratedCopyEntry(rung.draftKey, mapping?.sequenceId));
  const isGenerating = generation?.status === 'pending';
  /**
   * U7b — whether the "Generate with AI" drawer is open for THIS exact
   * (rung, sequence) pair. Same rung-scoping reasoning as `generation`
   * above: a session opened on a sibling rung mapping the same sequence
   * must not pop this rung's drawer open too.
   */
  const isSessionOpenHere = useGenerateWithAIStore(
    selectIsSessionOpenFor(rung.draftKey, mapping?.sequenceId ?? ''),
  );
  /**
   * U7b — the `stagedGeneration` seedKey ingredient `SequenceStepEditor`'s
   * `review` prop requires (its own doc; Upstream Discovery from U7a): must
   * bump ONLY when a step's `review.staged` is REPLACED (a fresh
   * generate/regenerate), never on the rep's own edit/decide keystrokes.
   * `generated-copy-store.ts` has no store-level counter for this (by
   * design — see that store's own module doc on why review DECISIONS live
   * there but this bead doesn't add a new field to it), so this shell tracks
   * it itself: every time a NEW `'pending'` token appears for this rung's
   * scope+sequence (a fresh `startCopyGeneration` call, from the drawer),
   * bump once. `lastPendingTokenRef` is the de-dup key across the several
   * renders a single pending generation produces.
   */
  const [stagedGeneration, setStagedGeneration] = useState(0);
  const pendingToken = generation?.status === 'pending' ? generation.token : null;
  const lastPendingTokenRef = useRef<number | null>(null);
  useEffect(() => {
    lastPendingTokenRef.current = null;
  }, []);
  useEffect(() => {
    if (pendingToken === null || pendingToken === lastPendingTokenRef.current) return;
    lastPendingTokenRef.current = pendingToken;
    setStagedGeneration((n) => n + 1);
  }, [pendingToken]);

  /**
   * U7b — this rung's segmentation conditions, fetched ONLY to seed the
   * "Generate with AI" drawer's audience default
   * (`defaultStatusesFromConditions`, `generate-with-ai-logic.ts`) — a rep
   * who segmented on "Registered OR Attended" almost certainly wants a
   * message scoped to those statuses. Not the sequence-copy `generate-copy`
   * request path itself (that stays scoped to `campaignSfdcId`); this is
   * purely a configure-form UX default, so a loading/errored segmentation
   * falls back to `null` (`defaultStatusesFromConditions(null)` is `[]`, an
   * empty default the rep can still fill in by hand) rather than blocking
   * anything.
   */
  const segmentationQuery = useQuery({
    queryKey: segmentationDetailQueryKey(rung.segmentationId),
    queryFn: () => getSegmentation(apiFetch, rung.segmentationId),
    // B8 (review #8) — this fetch exists ONLY to seed the "Generate with AI"
    // drawer's audience default; it has no other reader. Fetching it on
    // every rung mount regardless of whether that drawer is even open was
    // an unconditional, usually-wasted request.
    enabled: isSessionOpenHere,
  });
  const rungConditions = segmentationQuery.data?.head?.conditions ?? null;

  // Play isolation (plan 2026-09-25-002, U5) — the sequence read takes no
  // `playId`: a play's sequences are its own identities (Decision 12), so
  // this is the same unscoped read the playbook doorway uses. `renderedVersion`
  // below, the "Unsaved changes" diffing and the save's CAS token all follow
  // from this read.
  const sequenceQuery = useQuery({
    queryKey: sequenceDetailQueryKey(mapping?.sequenceId ?? ''),
    queryFn: () => getSequence(apiFetch, mapping!.sequenceId),
    enabled: !!mapping,
  });

  /**
   * The one thing every read below goes through: `{ versionId, versionNumber,
   * steps }` for the Sequence's current head. `null` while it is still loading
   * (or genuinely absent), which the render treats exactly as it always treated a
   * missing head — a spinner, not an empty editor a rep could type into and save
   * against the wrong parent.
   *
   * 2026-09-02 rip-out (U3) — this used to resolve either a lock-at-send pin
   * or an in-play branch row ahead of the head, via a second
   * `sequence-version` fetch. Both concepts are gone: every rung renders
   * exactly `sequenceQuery.data?.head`, unconditionally, so there is nothing
   * left to resolve.
   */
  const renderedVersion = sequenceQuery.data?.head
    ? {
        versionId: sequenceQuery.data.head.versionId,
        versionNumber: sequenceQuery.data.head.versionNumber,
        steps: sequenceQuery.data.head.steps,
      }
    : null;
  const isRenderedVersionLoading = sequenceQuery.isLoading;
  const isRenderedVersionError = sequenceQuery.isError;

  const isGong = mapping?.channel === 'gong_engage';
  // The real, server-bound fact — NOT local component state. This is the
  // only source `lockedFlowId` ever reads from (PR C / U4B; see module doc).
  const lockedFlowId = sequenceSummary?.flowId ?? null;

  /**
   * The Subject field's reply-step rule, derived ONCE for this whole rung
   * rather than per step editor (2026-09-17).
   *
   * Gong REJECTS a subject on a reply step and 400s the ENTIRE enrollment, so
   * `send-payload.ts#toOverrideSteps` drops it — and the editor must not offer
   * the field in the first place. Reply-ness is the step's ORDINAL in the
   * position-sorted sequence against the BOUND FLOW's live reply-step orders,
   * which is exactly what the send obeys; the step's own `gongStepDescriptor`
   * is NOT the authority (it is the imported step's flag and a reorder makes
   * it lie). See `gong-flow-picker-api.ts`'s "reply-step contract" note.
   *
   * `SequenceCopyPane.tsx` (the Library page's editor) derives it through the
   * SAME hook + helper pair, so the two hosts cannot drift apart.
   */
  const gongReplyOrders = useGongReplyStepOrders(mapping?.channel, lockedFlowId);
  const stepsInGongOrder = useMemo(
    () => sortStepsByPosition(renderedVersion?.steps ?? []),
    [renderedVersion?.steps],
  );

  /**
   * U10 (item 19, R13, landmine 7) — the roster-wide token-gap check.
   *
   * Runs against the LIVE copy — the saved head with any in-progress,
   * unsaved `SequenceStepEditor` drafts overlaid — the same "before Save, not
   * just after" guarantee the per-step check this supersedes documented, now
   * computed once across the whole sequence instead of per step. Subscribing
   * to the WHOLE draft-store `entries` map here (rather than each shell doing
   * its own `selectCopyDraftEntry` lookup twice) is what lets one combined
   * pass see every step's current value, not just the step a rep happens to
   * be typing in.
   *
   * `SamplePreviewCard` below is deliberately NOT changed to match — its own
   * doc comment already documents why it stays pinned to the saved head (a
   * stable "what a real send looks like today" reference).
   *
   * ── perf: U11 (E1) / Decision 14 ──────────────────────────────────────
   * The plan's original perf note here said "revisit with debouncing if that
   * changes". It changed (445-member campaigns in prod), and the fix is NOT a
   * debounce — a debounce would make the gap column lie for its own duration.
   * Three changes instead, none of which delays a write:
   *
   *  1. the draft subscription is SCOPED to this rung+sequence
   *     (`selectCopyDraftEntriesFor` + `useShallow`). It used to be the whole
   *     `entries` map, which `editCopyDraft` replaces per keystroke — so a
   *     keystroke in ANOTHER rung's editor re-ran this whole gap scan.
   *  2. `rosterTokenGaps` now runs against `useDeferredValue(liveSteps)`, so
   *     the keystroke's own render commits FIRST and the roster scan follows
   *     at a lower priority. The characters appear immediately; the "Missing
   *     fields" column catches up a beat later, which is honest (it is
   *     derived, not authoritative) rather than stale.
   *  3. the scan itself is O(len + M·tokens) rather than O(M·len)
   *     (`checkCopyVarGapsByMemberId`).
   *
   * Deferring `liveSteps` rather than the computed `rosterGaps` is deliberate
   * (the plan's own Deferred-to-Implementation default): it defers the
   * expensive WORK, not just the moment its result is shown.
   *
   * The real `campaign` (not `null`) is passed here — U10 fix: passing `null`
   * made every campaign-scoped token (`{{Event Name}}`/`{{Event Date}}`/
   * `{{Campaign Type}}`) resolve empty and false-positive as a roster gap for
   * EVERY member, every time, regardless of whether the campaign actually had
   * that data. The prototype avoids this by filtering campaign-scoped tokens
   * out of the per-member check entirely (`unresolvedTokenCounts`,
   * `playbooks-prototype-v6.html:3592-3605`, `.filter(t => d && !d.campaign)`);
   * this fix takes the alternative the plan chose instead — wire the real
   * campaign through — so a campaign-scoped token still correctly flags a gap
   * when the campaign itself is genuinely missing that field.
   */
  const savedSteps = renderedVersion?.steps;
  // U11 (E1) — scoped, shallow-compared subscription (see the doc above, (1)).
  // `mapping?.sequenceId ?? ''` for the unmapped case: no key can carry an
  // empty sequence id, so the selector correctly returns `{}`.
  const draftEntries = useSequenceCopyDraftStore(
    useShallow(selectCopyDraftEntriesFor(rung.draftKey, mapping?.sequenceId ?? '')),
  );
  /**
   * The LIVE steps of this rung's sequence — the saved head with THIS rung's
   * own unsaved drafts overlaid (scoped by `draftKey`, not just the sequence).
   * One array shared by Preview and the roster-gap check, so the two can never
   * disagree. See `mergeDraftsIntoSteps`' doc for the merge contract.
   */
  const liveSteps = useMemo(() => {
    if (!savedSteps || !mapping) return undefined;
    return mergeDraftsIntoSteps(savedSteps, draftEntries, rung.draftKey, mapping.sequenceId);
  }, [savedSteps, draftEntries, mapping, rung.draftKey]);
  /** Drives the "Unsaved changes" badge that stays visible in Preview mode. */
  const hasUnsavedCopy = useSequenceCopyDraftStore((state) =>
    sequenceHasCopyDrafts(state, rung.draftKey, mapping?.sequenceId),
  );
  // U11 (E1) — see the doc above, (2): the keystroke commits, then the roster
  // scan runs against the deferred value.
  const deferredLiveSteps = useDeferredValue(liveSteps);
  /**
   * U11 (E1) / Decision 14 — `bodyFormat` per step, computed once per steps
   * array instead of once per step per render.
   *
   * `gongStepBodyFormat` runs `isGongHtmlEnvelope` (a linear scan of the whole
   * body) plus `containsRealHtmlTags` — cheap for one step, but it was in the
   * `.map`'s JSX, so EVERY render of this shell re-scanned every step's body.
   * Keyed on the SAVED steps (not the live drafts) because that is what the
   * prop is derived from: a rep typing into step 1 does not change any step's
   * saved body, so this map is stable for the whole typing burst.
   */
  const bodyFormatByPosition = useMemo(() => {
    const byPosition = new Map<number, 'rich' | 'plain'>();
    for (const step of savedSteps ?? []) {
      byPosition.set(
        step.position,
        isGong ? gongStepBodyFormat(step.body, step.gongStepDescriptor) : 'plain',
      );
    }
    return byPosition;
  }, [savedSteps, isGong]);
  // R5-4 — the sender these steps will actually send as: the sequence's
  // live value once loaded, the rung's stale copy only during the loading
  // gap. See `effectiveSender`'s own doc in `sender-choice.ts`.
  const rosterGaps = useMemo(
    () => rosterTokenGaps(deferredLiveSteps ?? [], rosterMembers, campaign, effectiveSender(sequenceSummary, mapping)),
    // Deps are the primitives `effectiveSender` actually reads, not the
    // `sequenceSummary`/`mapping` objects — both are re-derived every render
    // (`.find()` / `rung.mapping`), and depending on the objects themselves
    // would recompute this on every render instead of only when the sender
    // truly changes.
    [deferredLiveSteps, rosterMembers, campaign, sequenceSummary?.sender, mapping?.sender],
  );
  /**
   * U11 (item 20, R13) — the member Preview mode actually interpolates for:
   * the rep's explicit pick if one exists, else the shell's own `sampleMember`
   * fallback (U6) so Preview mode isn't empty the first time it's toggled on.
   *
   * C3 widened the lookup from `rosterMembers` (this rung's matched subset) to
   * the whole campaign roster as a second pass, because the pick now comes from
   * the shared members table — and that table shows the FULL roster whenever no
   * rung is scoped (e.g. the moment the step opens). Resolving only against the
   * rung's subset silently ignored a click on any row outside it and previewed
   * the sample member instead, which reads as "clicking that row did nothing".
   */
  const previewMember =
    (selectedMemberId
      ? rosterMembers.find((m) => m.id === selectedMemberId) ??
        campaignMembers.find((m) => m.id === selectedMemberId)
      : undefined) ?? sampleMember;

  /**
   * C3 — entering Preview mode also SCOPES the shared members table to this
   * rung, so "Preview" filters the table down to the rung's real
   * first-match-wins membership rather than leaving it on the whole roster
   * (until C3 only Qualify's "Preview matches" set that scope). Leaving Preview
   * via the segmented control's Edit option deliberately leaves the scope
   * alone — the table showing this rung's members while the rep edits its copy
   * is accurate and useful; the explicit "Hide preview" button below is the one
   * affordance that unscopes.
   */
  function handleModeChange(next: 'edit' | 'preview') {
    setSequenceEditorMode(next);
    if (next === 'preview') onScopeToRung();
  }

  function handleHidePreview() {
    setSequenceEditorMode('edit');
    clearRungScope();
  }

  // Resolves the locked flow's display name from the live Gong catalog for
  // the read-only badge below. Best-effort: `playbooks_sequence` has no name
  // column (only `flow_id`), so a catalog miss (deleted/renamed flow) falls
  // back to `gongFlowDisplayName`'s generic phrase (plan 2026-09-28-008 U4,
  // R20/F9g — never the raw id) rather than blocking the locked-state render.
  const gongCatalogQuery = useQuery({
    queryKey: gongFlowsBrowseQueryKey(),
    queryFn: () => fetchGongFlowsBrowse(apiFetch),
    enabled: isGong && !!lockedFlowId,
  });
  const lockedFlowName = gongCatalogQuery.data?.flows.find((f) => f.flow_id === lockedFlowId)?.name ?? null;

  /**
   * Merges one edited step's values into the rendered version's full steps
   * array and posts the real whole-snapshot write — the plain main-line
   * `POST /v2/sequences/:id/versions` route, which moves the shared Sequence
   * head. Editing copy from any rung is now always this: whichever rung a rep
   * edits from, the save lands on the library head and every rung/play mapped
   * to this sequence sees it (2026-09-02 rip-out of the branch-on-edit
   * isolation mechanism — see
   * plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md). Diverging on
   * purpose is an explicit "Duplicate this sequence" action, not an implicit
   * side effect of which rung you happened to edit from.
   *
   * Thrown errors (most commonly a 409 `version_conflict`) invalidate the read
   * this save was composed from before rethrowing, so the next attempt reads a
   * fresh version — same reload-and-retry shape `ReviewStep.tsx`'s `handleSave`
   * already ships for the playbook save path.
   */
  /**
   * Round-2-followup plan, Unit A1 (Save-vs-Save-as-new-copy gate) — the
   * write path a step edit actually takes.
   *
   * Round-3-QA-fixes plan, Unit C3 / decision 9 — every save fetches usage
   * FRESH (`staleTime: 0`) before deciding, and acknowledges the FRESH
   * count: `places.length <= 1` (the sole-use case) is exactly today's
   * main-line save, no dialog at all — this must never become
   * confirm-every-save for the ordinary case.
   *
   * U2a rewrite — above `1`, this used to `await` an `openAlertDialog`
   * Promise that settled on the rep's button click. That Promise could
   * never survive the unmount a rail click inflicts on this whole shell
   * (`key={selectedRung.draftKey}`) or a route change — the root cause of a
   * dangling-Promise regression shipped twice (`isSaving` stuck `true`).
   * It now instead publishes the pending request to
   * `usage-sidebar-store.ts` (`openUsageSidebar`) and throws
   * {@link UsageSidebarOpenedError} SYNCHRONOUSLY (KTD 2) — nothing here
   * ever waits on the rep's choice again. `use-usage-sidebar-decision.ts`'s
   * hook, mounted below, is what actually executes "Update all"/"Save a new
   * version" later, from this same origin, however long after this call
   * returns.
   *
   * The sentinel must reach a TERMINAL catch un-decorated — `handleSaveStep`
   * below rethrows it after its own (harmless either way) query
   * invalidation, and `SequenceStepEditor.handleSave` is the terminal catch
   * that swallows it via `isUsageSidebarOpened`.
   *
   * Plan 2026-09-27-006 U7a (Requirement R5, Key Technical Decisions 3, 12)
   * — in play context (`playId` defined) an edit to a sequence THIS PLAY
   * already owns (a copy, or a play-created original) writes directly, no
   * gate at all: {@link savePlayWithGate}. An edit to a Library sequence the
   * play's ladder still shares fetches usage fresh and gates on the SAME
   * threshold the playbook doorway uses — sole use silently forks a private
   * copy, shared opens the usage sidebar with a `'play'` origin so the rep
   * can choose "Update all" (a template write) or "Save as new" (this
   * play's own copy). Every caller (`handleSaveStep`, `handleAddFirstStep`,
   * the AI drawer's `onApply`) follows this path automatically.
   */
  function saveWithGate(
    steps: SequenceStep[],
    generationInputs?: PlaybooksGenerationInputs,
  ): Promise<PlaybooksSaveSequenceStepsResponse> {
    if (playId !== undefined) {
      return savePlayWithGate(playId, steps, generationInputs);
    }
    return saveTemplateWithGate(steps, generationInputs);
  }

  /**
   * The identity-save half of {@link saveWithGate}: `saveSequenceSteps`
   * behind the usage gate/sidebar. Plan 2026-09-27-006 U7a — doorway-only:
   * the play path now always goes through {@link savePlayWithGate} /
   * {@link savePlayCopy} instead (the copy-on-write endpoint, which also
   * accepts seeding a first step — Decision 12 — so `handleAddFirstStep` no
   * longer needs this template-shaped path either).
   *
   * CAS token: `renderedVersion.versionId`, the identity's own head.
   */
  function saveTemplateWithGate(
    steps: SequenceStep[],
    generationInputs?: PlaybooksGenerationInputs,
  ): Promise<PlaybooksSaveSequenceStepsResponse> {
    const sequenceId = mapping!.sequenceId;
    const templateHeadVersionId = renderedVersion!.versionId;

    async function attemptWithPlaces(
      places: PlaybooksUsagePlace[],
      ownVersionPlays: PlaybooksUsageResponse['ownVersionPlays'],
      templatePlaybooks: PlaybooksUsageResponse['templatePlaybooks'],
    ): Promise<PlaybooksSaveSequenceStepsResponse> {
      // Plan 2026-09-28-007 U7 (rulebook R5, R14) — `origin` here is always
      // the `'playbook'` variant (`playId === undefined` is what routes here
      // at all). {@link gateInPlayWrite} is the ONE client gate every
      // in-play/in-playbook save goes through: it also confirms the
      // authoring rung ITSELF is still among `places` (`'stale'` otherwise)
      // and falls back to the plain count-only classification for
      // `origin.rungId === null` (a rung staged this session, no server id
      // yet, nothing to match).
      if (origin.kind === 'library') {
        throw new Error('unreachable: saveTemplateWithGate never runs for a library origin');
      }
      // Plan 2026-09-28-008 U2 (R14) — the Save panel's input, built once:
      // the menu, a stale refusal and a silent write's 409 all open it
      // (the last two already stale, holding the rep's steps). Nothing
      // reloads here; only the rep's "Reload to see their change" does.
      const openSavePanel = (staleRefusal?: string): never => {
        openUsageSidebarFromUsage({ places, ownVersionPlays, templatePlaybooks }, origin, sequenceSidebarTarget(sequenceId, sequenceSummary), {
          draftSteps: steps,
          headVersionId: templateHeadVersionId,
          // U7a passthrough (terra-qyt.9) — `undefined` for every ordinary
          // hand-edit save (every existing caller of `saveWithGate` omits the
          // second argument); non-`undefined` only for an "Apply to sequence"
          // write, so the usage sidebar's later "Update all"/"Save a new
          // version" write can forward the SAME provenance.
          generationInputs,
          // Codex fixes 2026-09-17, F2 — see `PendingUsageSidebar.stagedReview`.
          // `generation` is THIS rung+sequence's live entry (`:779`); an
          // AI-Apply always arrives with it `'ready'` (the drawer's Apply is
          // disabled otherwise), so the `status` check is a type narrowing.
          stagedReview: generationInputs !== undefined && generation?.status === 'ready' ? generation : undefined,
          ...(staleRefusal === undefined ? {} : { staleRefusal }),
        });
        throw new UsageSidebarOpenedError();
      };
      const gate = gateInPlayWrite({ places, origin });
      if (gate === 'menu') return openSavePanel();
      if (gate === 'stale') return openSavePanel(GROUP_MOVED_SENTENCE);
      try {
        const response = await saveSequenceSteps(
          apiFetch,
          sequenceId,
          steps,
          templateHeadVersionId,
          places.length,
          generationInputs,
          distinctUnsentPlays(places),
        );
        applySequenceHeadToCache(queryClient, sequenceId, response);
        return response;
      } catch (error) {
        if (isVersionConflictError(error)) return openSavePanel(error instanceof Error ? error.message : GROUP_MOVED_SENTENCE);
        throw error;
      }
    }

    async function attempt(retried: boolean): Promise<PlaybooksSaveSequenceStepsResponse> {
      const usage = await queryClient.fetchQuery({
        queryKey: sequenceUsageQueryKey(sequenceId),
        queryFn: () => getSequenceUsage(apiFetch, sequenceId),
        staleTime: 0,
      });
      try {
        return await attemptWithPlaces(usage.places, usage.ownVersionPlays ?? [], usage.templatePlaybooks ?? []);
      } catch (error) {
        if (isSharedMappingConflictError(error) && !retried) {
          await queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(sequenceId) });
          return attempt(true);
        }
        if (isSharedMappingConflictError(error)) {
          // A SECOND consecutive conflict — decision 9's bounded retry gives
          // up. Throw a NEW error carrying only the plain text (never the
          // original conflict's raw server `detail`), because the caller
          // chain above this (`handleSaveStep`'s catch, then
          // `SequenceStepEditor`'s own generic catch) surfaces `error.message`
          // verbatim in its own toast — re-throwing the ORIGINAL error would
          // let that raw `detail` leak through a second, later toast even
          // though this catch never showed it directly.
          throw new Error(
            "This sequence's places changed again while you were saving. Please try once more.",
          );
        }
        throw error;
      }
    }

    return attempt(false);
  }

  /**
   * The play-context half of {@link saveWithGate} — plan 2026-09-28-006 U4
   * (rulebook R5: gate by USE, never by ownership). Every in-play steps save
   * (a hand edit, AI Apply, "Load steps from Gong", "Add first step") fetches
   * usage fresh FIRST, whoever owns the sequence:
   *
   *  - used by this group only ({@link gateInPlayWrite} returns `'silent'`) →
   *    silent: an owned sequence (a copy, or a play-created original) is
   *    written in place (`mode: 'ensure'`, no `rungId` — the server writes an
   *    owned id directly); a Library sequence forks for THIS group only
   *    (`rungId`, `mode: 'ensure'` — R3), in the same request.
   *  - anything else — including a copy two groups of this play share (the
   *    R5 defect the old "owned → write directly" shortcut caused) → the
   *    Save menu, with `target.owningPlayId` / `target.archivedInLibrary`
   *    set so `saveMenuOptions` shows exactly the choices that apply.
   *  - [R14] the origin group itself is gone from `places` (moved, remapped
   *    or removed since the rep loaded the page) → `gateInPlayWrite` returns
   *    `'stale'` and the SAME Save panel input opens already stale with the
   *    plain "group moved" sentence — never a write, never a menu offered
   *    against a group that no longer means what the rep thinks it does
   *    (Codex c3-2), and (plan 2026-09-28-008 U2) never a reload: the rep's
   *    steps stay in the editor and on the pending until they press
   *    "Reload to see their change". A silent write refused with a 409
   *    `version_conflict` opens the same panel stale with the server's
   *    sentence.
   */
  async function savePlayWithGate(
    targetPlayId: string,
    steps: SequenceStep[],
    generationInputs: PlaybooksGenerationInputs | undefined,
  ): Promise<PlaybooksSaveSequenceStepsResponse> {
    const sequenceId = mapping!.sequenceId;
    const libraryHeadVersionId = renderedVersion!.versionId;
    const usage = await queryClient.fetchQuery({
      queryKey: sequenceUsageQueryKey(sequenceId),
      queryFn: () => getSequenceUsage(apiFetch, sequenceId),
      staleTime: 0,
    });
    const places = usage.places;
    if (origin.kind === 'library') {
      throw new Error('unreachable: savePlayWithGate never runs for a library origin');
    }
    const openSavePanel = (staleRefusal?: string): never => {
      openUsageSidebarFromUsage(usage, origin, sequenceSidebarTarget(sequenceId, sequenceSummary), {
        draftSteps: steps,
        headVersionId: libraryHeadVersionId,
        generationInputs,
        stagedReview: generationInputs !== undefined && generation?.status === 'ready' ? generation : undefined,
        ...(staleRefusal === undefined ? {} : { staleRefusal }),
      });
      throw new UsageSidebarOpenedError();
    };
    // Plan 2026-09-28-007 U3 (R3) — an explicit mode always names its
    // group, owned or Library: the server checks the group still uses the
    // sequence before writing. {@link gateInPlayWrite} throws
    // {@link SAVE_GROUP_FIRST} for a rung staged this session (no server id
    // yet — can't be matched against `places`) before asking the gate a
    // question it can't answer.
    const gate = gateInPlayWrite({ places, origin });
    if (gate === 'menu') return openSavePanel();
    if (gate === 'stale') return openSavePanel(GROUP_MOVED_SENTENCE);
    try {
      // `gateInPlayWrite` already threw `SAVE_GROUP_FIRST` above for
      // `rung.id === undefined` (a staged group) — a 'silent' result
      // guarantees a real id, the same guarantee the deleted explicit guard
      // gave the type checker. Plan 2026-09-29-001 U1 (R3/R5/R14) — a silent
      // write also names the group SET it was checked against; a sole-use
      // write is checked against exactly the one group making it.
      return await savePlayCopy(targetPlayId, sequenceId, steps, generationInputs, {
        rungId: rung.id!,
        mode: 'ensure',
        confirmedRungIds: [rung.id!],
      });
    } catch (error) {
      if (isVersionConflictError(error)) return openSavePanel(error instanceof Error ? error.message : GROUP_MOVED_SENTENCE);
      // Plan 2026-09-29-001 U1 (R5/R8) — a second group started sharing the
      // piece between the usage read above and this write: refetch usage
      // fresh and reopen the Save menu on the CURRENT list, instead of
      // toasting a write the rep never chose to fan out.
      if (isSharedMappingConflictError(error)) {
        const freshUsage = await queryClient.fetchQuery({
          queryKey: sequenceUsageQueryKey(sequenceId),
          queryFn: () => getSequenceUsage(apiFetch, sequenceId),
          staleTime: 0,
        });
        openUsageSidebarFromUsage(freshUsage, origin, sequenceSidebarTarget(sequenceId, sequenceSummary), {
          draftSteps: steps,
          headVersionId: libraryHeadVersionId,
          generationInputs,
          stagedReview: generationInputs !== undefined && generation?.status === 'ready' ? generation : undefined,
        });
        throw new UsageSidebarOpenedError();
      }
      throw error;
    }
  }

  /**
   * The copy-on-write write itself, shared by {@link savePlayWithGate}'s
   * owned/sole-use branches, `handleAddFirstStep`, and
   * `handleUsageSidebarSaveNew`'s play branch. CAS token: the head of the
   * identity this edit was composed from, `renderedVersion.versionId` — the
   * play's own copy's head when already owned, the Library head otherwise
   * (Key Technical Decision 4 translates a Library-head CAS onto a fresh
   * fork's copy automatically).
   *
   * The play-copy response (`{ playId, sequenceId, versionId, steps, source,
   * playUrl, forkedFrom, ladder }`) is adapted to `saveWithGate`'s existing
   * return shape (`{ id, versionId, versionNumber: null, steps }`) so no
   * caller changes. The cache patch always targets `response.sequenceId` —
   * the COPY's own key — never the Library id's when `forkedFrom` is set
   * (a fresh fork's steps belong to the copy, not the Library row the rep
   * addressed).
   *
   * Plan 2026-09-27-007 U4 — a fork (`forkedFrom !== null`) is adopted
   * through `ensure-play-owned.ts#adoptPlayLadderMoves` BEFORE this returns: the
   * rewritten ladder, the play library invalidation, AND the rekey of every
   * unsaved copy draft / staged AI review from the Library id onto the copy
   * id — so the other steps' typing follows the rung instead of being
   * orphaned under the Library id (where the unsaved-work guard would count
   * it forever). The just-saved steps' drafts are then cleared under the
   * COPY key: `SequenceStepEditor.handleSave`'s own
   * `clearCopyDraftIfUnchanged(draftKey, …)` uses the `draftKey` it captured
   * at render (the Library id's key), which the rekey has already emptied —
   * a harmless no-op — so this is the clear that actually lands.
   *
   * Plan 2026-09-28-008 U2 (R14) — a 409 `version_conflict` is rethrown
   * untouched: nothing here refetches or adopts on a refusal. The caller
   * decides — {@link savePlayWithGate} opens its Save panel stale; a Save
   * menu decision's rethrow parks the pending stale in the decision hook.
   */
  async function savePlayCopy(
    targetPlayId: string,
    sequenceId: string,
    steps: SequenceStep[],
    generationInputs: PlaybooksGenerationInputs | undefined,
    /**
     * Plan 2026-09-28-007 U3 (R3) — `rungId` names THIS group on every save,
     * owned or Library. `confirmedRungIds` (plan 2026-09-29-001 U1, R5/R8) —
     * the group set this write was checked against.
     */
    options: { rungId: string; mode: 'ensure' | 'fork'; confirmedRungIds?: string[] },
  ): Promise<PlaybooksSaveSequenceStepsResponse> {
    const expectedVersionId = renderedVersion!.versionId;
    const response = await savePlaySequenceCopy(
      apiFetch,
      targetPlayId,
      sequenceId,
      steps,
      expectedVersionId,
      generationInputs,
      options,
    );
    const adapted: PlaybooksSaveSequenceStepsResponse = {
      id: response.sequenceId,
      versionId: response.versionId,
      versionNumber: null,
      steps: response.steps,
    };
    applySequenceHeadToCache(queryClient, response.sequenceId, adapted);
    if (response.forkedFrom !== null || response.ladder !== null) {
      await adoptPlayLadderMoves(queryClient, {
        playbookId,
        playId: targetPlayId,
        kind: 'sequence',
        ladder: response.ladder,
        moves:
          response.forkedFrom === null
            ? []
            : [{ rungId: options.rungId, fromId: response.forkedFrom, toId: response.sequenceId }],
        drafts: 'carry',
      });
    }
    if (response.forkedFrom !== null) {
      for (const step of steps) {
        clearCopyDraftIfUnchanged(copyDraftKey(rung.draftKey, response.sequenceId, step.position), {
          subject: step.subject ?? '',
          body: step.body ?? '',
        });
      }
    }
    return adapted;
  }

  /**
   * U2a (R3) — draft restore on return. A rep who follows a "Where is this
   * used" link out of the sidebar (to inspect another place) and back loses
   * nothing typed here EXCEPT when that trip crossed a different Play
   * (`PlayFlowShell`'s `[playId]` effect clears every copy draft on that
   * boundary — `usage-sidebar-store.ts`'s module doc, KTD 1). The pending
   * sidebar request itself survives that unconditionally (it lives in this
   * separate store, not the copy-draft one) — this re-seeds the copy draft
   * FROM the carried `draftSteps` the moment this shell can see a real head
   * to diff against, ONLY for a position with no draft store entry already
   * (never clobber typing the rep did after returning) and ONLY where the
   * carried value actually differs from the head (never a phantom "Unsaved
   * changes" for a position that already matches).
   *
   * Codex fixes 2026-09-18, F4 — gated on `originBelongsToFlow` (below): a
   * sibling play of the same playbook shares this rung's `draftKey`, so
   * without it a pending staged on Play A would be re-seeded into the keys
   * Play B's editor reads. Still load-bearing after QA round 8's U7 removed
   * the async `'resume-edit'` handler — this effect is the MANUAL round trip's
   * restore (a rail click away and back), and it fires regardless of
   * `decision`.
   */
  const restoredDraftRef = useRef(false);
  useEffect(() => {
    restoredDraftRef.current = false;
  }, []);
  useEffect(() => {
    if (restoredDraftRef.current) return;
    if (!renderedVersion || !mapping) return;
    const pending = useUsageSidebarStore.getState().pending;
    if (!pending || pending.mode !== 'save' || pending.draftSteps === null) return;
    // terra-qyt.14 (U9) — `'library'` is the only origin this restore does
    // NOT apply to; a `'play'` and a `'playbook'` origin describe the SAME
    // editing surface (`usage-sidebar-store.ts`'s own doc), so both must flow
    // through here identically.
    if (pending.origin.kind === 'library' || pending.origin.rungDraftKey !== rung.draftKey) return;
    // Codex fixes 2026-09-18, F4 — the flow-identity gate (originally shared
    // with the round-7 async handler, deleted in QA round 8): a sibling play
    // of the same playbook
    // shares this rung's `draftKey`, so a pending staged on Play A would
    // otherwise be re-seeded into the keys Play B's editor reads the moment
    // this shell (re)mounts on B with a head — this effect fires "regardless
    // of `decision`" (its doc above) and reads `pending` off the store, so
    // nothing else stood between it and the wrong play. `originBelongsToFlow`
    // is the predicate `SequencesStep`'s decision gate already uses; a
    // `'play'` origin matches only its own `playId`, a `'playbook'` origin
    // only the playbook doorway (no `playId`). Not setting the ref: on the
    // origin play this must still fire once.
    if (!originBelongsToFlow(pending.origin, { playId, playbookId })) return;
    if (pending.target.id !== mapping.sequenceId) return;
    restoredDraftRef.current = true;
    for (const draftStep of pending.draftSteps) {
      const headStep = renderedVersion.steps.find((s) => s.position === draftStep.position);
      if (!headStep) continue;
      const headValues = { subject: headStep.subject ?? '', body: headStep.body ?? '' };
      const carriedValues = { subject: draftStep.subject ?? '', body: draftStep.body ?? '' };
      if (carriedValues.subject === headValues.subject && carriedValues.body === headValues.body) continue;
      const key = copyDraftKey(rung.draftKey, mapping.sequenceId, draftStep.position);
      if (useSequenceCopyDraftStore.getState().entries[key] !== undefined) continue;
      acceptGeneratedCopyDraft(key, headValues, carriedValues);
    }
  }, [renderedVersion, mapping, rung.draftKey]);

  /**
   * U11 (E1) / Decision 14 — `useCallback`, so the `onSave` prop handed to
   * every `SequenceStepEditor` below is reference-stable across renders.
   * Without it, `React.memo` on that editor could never skip a re-render: a
   * fresh function identity per render defeats the props comparison entirely,
   * which meant a keystroke in step 1 re-rendered steps 2..N as well.
   *
   * `saveWithGate` is a plain function declaration (a closure over `mapping`/
   * `renderedVersion`/`origin`), so it is deliberately NOT in the dependency
   * list — including it would make this callback change identity on every
   * render again and undo the whole point. What IS depended on is exactly the
   * data this function reads: the rendered version, the mapping's sequence id
   * (for the error-path invalidations), and the query client.
   * `saveWithGateRef` keeps the invoked closure current without being a
   * dependency, which is what makes that safe rather than stale.
   */
  const saveWithGateRef = useRef(saveWithGate);
  saveWithGateRef.current = saveWithGate;
  /**
   * The sequence id the error path invalidates. A plain scalar (rather than
   * `mapping!.sequenceId` inline) so it can be a real dependency of the
   * callback below — `mapping` is an object rebuilt per render.
   * `handleSaveStep` is only reachable from a step editor, which only renders
   * for a MAPPED rung, so the `''` branch is unreachable in practice; it is
   * spelled rather than asserted so this stays a total expression.
   */
  const sequenceIdForInvalidate = mapping?.sequenceId ?? '';
  const handleSaveStep = useCallback(
    async (position: number, values: { subject: string; body: string }): Promise<SequenceStep> => {
      if (!renderedVersion) {
        throw new Error("This sequence hasn't finished saving yet. Reload and try again.");
      }
      const steps = renderedVersion.steps.map((s) =>
        s.position === position ? { ...s, subject: values.subject, body: values.body } : s,
      );
      try {
        const response = await saveWithGateRef.current(steps);
        const saved = response.steps.find((s) => s.position === position);
        if (!saved) throw new Error('Saved, but the step is missing from the response — reload to confirm.');
        return saved;
      } catch (error) {
        await queryClient.invalidateQueries({
          queryKey: sequenceDetailQueryKey(sequenceIdForInvalidate),
        });
        await queryClient.invalidateQueries({
          queryKey: sequenceUsageQueryKey(sequenceIdForInvalidate),
        });
        throw error;
      }
    },
    [renderedVersion, sequenceIdForInvalidate, queryClient],
  );

  /**
   * C1 — the "Load steps from Gong" retry affordance: this sequence is
   * ALREADY flow-bound (`lockedFlowId` set) but has no steps, either because
   * an earlier import failed between the bind and the save, or because it is
   * a pre-existing flow-bound sequence that never got them. Nothing to lose
   * (the sequence has zero steps by construction here), so no confirm —
   * straight to `importGongFlowSteps` (fetch real steps → re-bind the SAME
   * flow, idempotent → save). The first-bind picker flow (a NEW flow pick)
   * has no caller in this batch — that's D2, Batch 6; this retry is the one
   * live caller of `importGongFlowSteps` today.
   *
   * Round-3-QA-fixes plan, Codex adversarial-review finding F2 — this used to
   * call `importGongFlowSteps` with no `confirmedMappingCount` at all, so on
   * a sequence used in 2+ places the server's shared-save recheck defaulted
   * to 1 and always refused with `shared_mapping_conflict` (root cause (a) in
   * the plan's "Root cause" section). Decision 10's preferred pattern (a
   * fresh `getSequenceUsage` fetch, acknowledging `places.length`) is used
   * here rather than the cached `sequenceQuery.data?.activeMappingCount` —
   * the same fetch-fresh-before-every-shared-write shape `saveWithGate`
   * already uses, so this retry can't 409 on a stale pre-check either. A
   * `shared_mapping_conflict` that still slips through (the fresh count
   * changed again between this fetch and the write) is sanitized to plain
   * copy rather than the server's raw `detail` reaching the toast — decision
   * 3.
   *
   * That template-shaped path is the playbook DOORWAY's only (`playId`
   * undefined).
   *
   * Plan 2026-09-27-007 U4 (Key Decision 10) — inside a play, loading steps
   * is a step save like any other: fetch Gong's steps for the flow this
   * sequence is already bound to (no re-bind — `lockedFlowId` IS the
   * binding) and hand them to {@link saveWithGate}, so the one in-play rule
   * applies. A play-owned sequence saves straight onto the copy; a sole-use
   * Library sequence forks silently into this play's copy (the copy endpoint
   * keeps the caller's Gong step descriptors when the parent has no steps);
   * a shared one opens the usage sidebar, where the rep chooses "Update N
   * uses" or "Save as new" — that sidebar taking over is not an error, so it
   * returns silently. The import path is never used in a play: it writes the
   * addressed identity directly, which for a Library sequence carrying
   * `?playId=` the ownership check refuses.
   */
  async function handleLoadStepsFromGong() {
    if (!lockedFlowId || !renderedVersion) return;
    const sequenceId = mapping!.sequenceId;
    setIsImportingSteps(true);
    try {
      if (playId !== undefined) {
        const { steps: gongSteps } = await fetchGongFlowSteps(apiFetch, lockedFlowId, { force: true });
        const save = await saveWithGate(gongStepsToSequenceSteps(gongSteps));
        applySequenceHeadToCache(queryClient, save.id, save);
        addToast({
          content: `Loaded ${save.steps.length} steps from "${gongFlowDisplayName(lockedFlowName)}".`,
          status: 'success',
        });
        return;
      }
      const { places } = await queryClient.fetchQuery({
        queryKey: sequenceUsageQueryKey(sequenceId),
        queryFn: () => getSequenceUsage(apiFetch, sequenceId),
        staleTime: 0,
      });
      const { save } = await importGongFlowSteps(apiFetch, {
        sequenceId,
        flowId: lockedFlowId,
        headVersionId: renderedVersion.versionId,
        confirmedMappingCount: places.length,
        // Round-4 QA codex-review fix (wave 1, finding 1) — the unsent-play
        // axis, read off the SAME fresh usage fetch above rather than a
        // second request: `distinctUnsentPlays` is the client twin of the
        // server's `countUnsentPlaysForSequence` recheck. Without it the
        // server defaulted to `1` and this retry 409'd on any sequence whose
        // playbook already carried 2+ draft plays.
        confirmedUnsentPlayCount: distinctUnsentPlays(places),
      });
      applySequenceHeadToCache(queryClient, sequenceId, save);
      addToast({
        content: `Loaded ${save.steps.length} steps from "${gongFlowDisplayName(lockedFlowName)}".`,
        status: 'success',
      });
    } catch (error) {
      if (isUsageSidebarOpened(error)) return;
      addErrorToast({
        content: isSharedMappingConflictError(error)
          ? "This sequence's places changed while loading steps. Reload and try again."
          : error instanceof Error
            ? error.message
            : 'Could not load steps from Gong — try again.',
      });
    } finally {
      setIsImportingSteps(false);
    }
  }

  /**
   * QA round 8, item 6 — the play flow's own doorway out of "No steps on
   * this sequence yet." for a legacy (pre-this-round) non-Gong sequence:
   * seeds the same one empty step `POST /sequences` now seeds at create
   * (`seedStepsForChannel`, shared with the server), through the SAME gate
   * every other hand-edit save goes through (`saveWithGate`) — so a sequence
   * shared by more than one rung still opens the usage sidebar here instead
   * of silently overwriting what another rung sees.
   *
   * Plan 2026-09-27-006 U7a — in a play this now goes through `saveWithGate`
   * (not the doorway-only `saveTemplateWithGate`): the copy-on-write endpoint
   * accepts seeding a first step (`stepPositionMismatchDetail` returns `null`
   * for a zero-step parent, U3.5), so `savePlayWithGate`'s ordinary
   * owned/sole-use/shared decision applies here exactly like a hand-typed
   * edit — a shared, multi-place Library sequence with zero steps still gets
   * the usage sidebar rather than a silent private copy.
   */
  async function handleAddFirstStep() {
    setIsAddingFirstStep(true);
    try {
      await saveWithGate(seedStepsForChannel(mapping!.channel));
      addToast({ content: 'Step added.', status: 'success' });
    } catch (error) {
      await queryClient.invalidateQueries({
        queryKey: sequenceDetailQueryKey(sequenceIdForInvalidate),
      });
      await queryClient.invalidateQueries({
        queryKey: sequenceUsageQueryKey(sequenceIdForInvalidate),
      });
      if (isUsageSidebarOpened(error)) return;
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not add a step — try again.',
      });
    } finally {
      setIsAddingFirstStep(false);
    }
  }

  /**
   * The mapping picker (Codex adversarial-review fix, PR C). Rendered for BOTH
   * states — unmapped ("Choose sequence") and mapped ("Change sequence") —
   * because both were previously impossible: `setPlaybookDraftRungMapping` had
   * no production caller at all, so a rung's sequence could only ever be
   * whatever the seed or a direct API call had put there.
   *
   * Unlike the Gong flow lock, this is NOT one-way: `rung.mapping` is a
   * playbook-draft field, so re-pointing a rung at a different sequence is an
   * ordinary edit that persists write-through, immediately.
   */
  const mappingModal = isMappingModalOpen ? (
    <SequenceMappingDrawer
      playbookId={playbookId}
      playId={playId}
      draftKey={rung.draftKey}
      rungId={rung.id ?? null}
      rungName={rungName}
      currentMapping={mapping}
      sequences={library?.sequences}
      initialChannelFilter={mappingModalChannelFilter}
      onClose={() => setIsMappingModalOpen(false)}
    />
  ) : null;

  /** C2 — opens the mapping drawer, optionally seeding its channel filter. */
  function openMappingModal(channelFilter?: LauncherChannel) {
    setMappingModalChannelFilter(channelFilter);
    setIsMappingModalOpen(true);
  }

  if (!mapping) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-border-primary bg-bg-primary p-4">
        <Text size="sm" variant="tertiary">
          No sequence mapped — members here don&apos;t get a send.
        </Text>
        <Button variant="secondary" size="sm" onClick={() => openMappingModal()}>
          Choose sequence
        </Button>
        {mappingModal}
      </div>
    );
  }

  /**
   * U5 (round-3-QA-fixes plan) — returns a mapped rung to the unmapped
   * "Choose sequence" state. Mirrors `confirmSwap`'s success branch
   * (`SequenceMappingDrawer.tsx:394-420`) minus the re-point: the same
   * write-through call, the same housekeeping order, guarded by a "landed"
   * check so a REJECTED write never runs any of it — the existing
   * `persistLadder` catch path already rolls the optimistic apply back,
   * which is what leaves "Change sequence" on screen after a failed unmap.
   * No confirm dialog (Decision 6 — a null mapping is reversible via "Choose
   * sequence"; unlike a swap, nothing here is permanently lost). The button
   * that calls this only renders past the `if (!mapping)` early return above,
   * so `!mapping` here is unreachable in practice — TS can't carry that
   * narrowing through a function declaration's hoisting, so it's re-checked
   * explicitly rather than asserted away.
   *
   * Round-2-fixes M2 — `landed` (not a bare `response` truthiness check) is
   * what makes this run its cleanup when a 409 retry discovers the fresh head
   * already unmapped the rung (`persistLadderOnce`'s retry-no-op returns
   * `null` in that case — `draftRungMapsToSequence(…, null)` is the
   * disambiguator, see its own doc comment). Before this fix, that `null`
   * looked identical to a genuine failure and this handler skipped even its
   * existing `clearCopyDrafts` cleanup — and never called `clearGeneratedCopy`
   * at all, leaving a staged Generate-with-AI review for the OLD rung/sequence
   * pair to reappear the instant the rung was remapped back onto that same
   * sequence (Codex adversarial review round 2, Might-Break #2).
   *
   * Round-3 (Codex MUST-ADDRESS) — `landed` alone is no longer enough to gate
   * the module-scope store cleanup (`clearCopyDrafts`/`clearGeneratedCopy`/
   * `closeGenerateSession`). Those keys (`generatedCopyKey`, `copyDraftKey`)
   * carry no playbook identity (Decision 1, unchanged), so if the rep
   * navigates away to a DIFFERENT playbook — including a duplicate that
   * retains this same rung id (`schemas.ts:164-165`) — before this unmap's
   * response lands, a non-null response would otherwise wipe whatever that
   * OTHER flow has since staged for the same rung/sequence pair. `reflectedHere`
   * (`draftRungMapsToSequence(playbookId, rung.draftKey, null)`) doubles as
   * the "is the flow still on THIS playbook" check for free: its first
   * statement is `state.playbookId !== playbookId → false`, and
   * `PlayFlowShell` swaps the draft store to the new playbook on navigation
   * (`clearPlaybookDraft` + `ensurePlaybookDraft`), so once the rep has moved
   * on, `reflectedHere` reads `false` regardless of what the response said.
   * The query-cache writes below (`invalidateQueries`, `applyLadderSaveToCache`)
   * stay gated on `landed` alone, unchanged: they are id-keyed
   * (`sequenceDetailQueryKey(oldSequenceId)`, the response's own
   * `playbookId`) and remain correct for playbook A no matter where the rep
   * is now — gating them on `reflectedHere` too would leave A's
   * `staleTime: Infinity` playbook cache stale on return (Decision 10).
   */
  function handleRemoveMapping() {
    if (!mapping || isRemovingMapping) return;
    const oldSequenceId = mapping.sequenceId;
    setIsRemovingMapping(true);
    setPlaybookDraftRungMapping(apiFetch, playbookId, rung.draftKey, null)
      .then((response) => {
        const reflectedHere = draftRungMapsToSequence(playbookId, rung.draftKey, null);
        const landed = response !== null || reflectedHere;
        if (!landed) return undefined;
        if (reflectedHere) {
          // Same reasoning as `confirmSwap`'s Bug 4 fix — clear the OLD
          // sequence's copy drafts and staged review (and close a Generate
          // session addressed to it) only once the write has actually
          // landed AND this flow's own draft still reflects the mapping
          // that was requested (round-3 MUST-ADDRESS, see doc above).
          clearCopyDrafts(rung.draftKey, oldSequenceId);
          clearGeneratedCopy(rung.draftKey, oldSequenceId);
          if (isSessionOpenHere) closeGenerateSession();
        }
        void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(oldSequenceId) });
        return response ? applyLadderSaveToCache(queryClient, playbookId, response, playId) : undefined;
      })
      .catch((error) => {
        addErrorToast({
          content: error instanceof Error ? error.message : 'Could not remove this mapping — try again.',
        });
      })
      .finally(() => setIsRemovingMapping(false));
  }

  /**
   * U1 (R-L3, R-D4) — Rename, reached through the header's kebab
   * (`sequence-card-menu.ts#getSequenceCardMenuItems`). Non-version-producing
   * (`renameSequence` -> `PATCH /v2/sequences/:id`, same call `PlaybooksConfigPage.tsx`
   * makes), so the write is a plain patch-in-place + invalidate on the shared
   * `libraryQueryKey()` cache (that query is this card's own `sequenceSummary`
   * source, via `library.sequences.find(...)` above) — the same precedent
   * `PlaybooksConfigPage.tsx#updateLibraryRowName`/`invalidateLibrary` set.
   * (`renameOpen`/`savingRename` state itself is declared earlier, above the
   * `if (!mapping)` early return — see that declaration's own doc.)
   *
   * Plan 2026-09-28-006 U4 (rulebook R5, R6) — inside a play a rename is an
   * ordinary in-play edit: it goes through the SAME usage gate as every
   * other sequence edit (`saveSequenceSettingsWithGate`). Only this group
   * uses it → silent (owned: renamed in place with `{ playId }`; Library:
   * forked for THIS group, then the copy renamed). Anything else → the
   * Save menu opens with a `{ field: 'name' }` pending and the drawer
   * closes; the rep's choice does the write (`handleSettings*`). The
   * playbook doorway (`playId` undefined) keeps today's direct rename and
   * its "renaming changes it everywhere" caption.
   */
  function handleRenameSave(name: string) {
    if (!mapping) return;
    const sequenceId = mapping.sequenceId;
    setSavingRename(true);
    // `group` and `playId` are either both present (in play) or both absent
    // (outside one) — matching `SequenceSettingsWriteOptions`'s union exactly.
    const rename = (id: string, group?: { rungId: string; confirmedRungIds?: string[] }) =>
      (group === undefined
        ? renameSequence(apiFetch, id, name, { playId: undefined })
        : renameSequence(apiFetch, id, name, { playId: playId!, group })
      ).then(() => id);
    (playId !== undefined
      ? saveSequenceSettingsWithGate(apiFetch, queryClient, {
          sequenceId,
          sequenceName: sequenceSummary?.name,
          usageOrigin: origin,
          owningPlayId: sequenceSummary?.owningPlayId ?? null,
          archivedInLibrary: sequenceSummary !== undefined && isArchivedInLibrary(sequenceSummary),
          draftSettings: { field: 'name', name },
          playId,
          playbookId,
          write: rename,
        })
      : rename(sequenceId).then((value) => ({ kind: 'written' as const, value }))
    )
      .then((response) => {
        if (response.kind === 'menu') {
          // The Save menu took over — the rep's choice there does the write.
          setRenameOpen(false);
          return;
        }
        // Plan 2026-09-29-001 U2 (R14, B5) — a refusal leaves the drawer
        // open on the name the rep typed; the Save panel carries the
        // sentence and the one Reload.
        if (response.kind === 'stale') return;
        const ownedId = response.value;
        // Plan 2026-09-27-006 U6 — `libraryQueryKey(playId)`, not the
        // unscoped key: inside a play this is the entry `usePlaybooksLibrary`
        // actually reads from (`sequenceSummary` above, via
        // `library.sequences.find(...)`), so the patch must land there or the
        // rename is invisible until the invalidation's refetch lands.
        // `libraryQueryKey(undefined)` (the playbook doorway) is unchanged.
        queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(playId), (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            sequences: prev.sequences.map((s) => (s.id === ownedId ? { ...s, name } : s)),
          };
        });
        void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
        addToast({ content: `Renamed to "${name}".`, status: 'success' });
        setRenameOpen(false);
      })
      .catch((error: unknown) => {
        addErrorToast({ content: error instanceof Error ? error.message : 'Could not rename — try again.' });
      })
      .finally(() => setSavingRename(false));
  }

  /**
   * L2/L3 (Decision 3) — computed once, unconditionally, so both the header's
   * Generate button (gated on `editorMode === 'edit'` below) and the
   * disabled-hint caption (kept in the flow-line column, not the button
   * cluster — Deferred-to-Implementation default) read the SAME result.
   */
  const generateTrigger = generateTriggerState({
    campaignSfdcId,
    isGenerating,
    hasHead: renderedVersion !== null,
  });

  /**
   * Round-4 QA fixes, U5 (S1b) / Decision 7 — the play flow had ZERO usage
   * discoverability before this (Config already showed it); computed once
   * so the header badge below, the Generate drawer's review footer, and the
   * apply toast all read the SAME number.
   */
  const usagePlacesText = usedInPlacesText(sequenceSummary?.usageCount ?? 0);

  return (
    <div className="flex flex-col gap-3">
      <CollapsibleCard open={isCardOpen} onOpenChange={setIsCardOpen} className="border-border-primary">
        <CollapsibleCardHeader>
          {/*
            Live-QA fixes, A2 — whole-header click-to-expand, wrapping BOTH
            of `CollapsibleCardHeader`'s previous direct children in one new
            container (no test in this file locates the header via a
            `.closest('div.flex...')` class chain — unlike `QualifyStep.tsx`'s
            "Segmentations" card, so there's no selector to preserve here;
            see that file's comment for the case where it mattered).
          */}
          <div
            className="flex min-w-0 flex-1 cursor-pointer items-start justify-between gap-3"
            tabIndex={0}
            onClick={(e) => {
              if (isInteractiveRowTarget(e.target)) return;
              setIsCardOpen((v) => !v);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              if (isInteractiveRowTarget(e.target)) return;
              e.preventDefault();
              setIsCardOpen((v) => !v);
            }}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <Text size="sm" weight="semibold">
                  Sequence — {sequenceSummary?.name ?? SEQUENCE_UNAVAILABLE_NAME}
                </Text>
                {/* Plan 2026-09-28-006 U4 ([R18]) — a Library piece archived while this play still uses it. */}
                {sequenceSummary && isArchivedInLibrary(sequenceSummary) && (
                  <StatusBadge status="neutral">Archived in Library</StatusBadge>
                )}
                {/* U5 (S1b, Decision 7) — discoverability badge, hidden for a sole-use sequence (`usedInPlacesText` returns `null` at N<=1). */}
                {usagePlacesText && <Badge>{usagePlacesText}</Badge>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge>{channelLabel(mapping.channel)}</Badge>
                {isGong && (
                  <Badge color="purple">
                    <BadgeIcon type={Lightning} />
                    Gong-backed
                  </Badge>
                )}
              </div>
              {/*
                2026-09-02 rip-out (U3, folding in the old plan's Round-2 B1) —
                this used to be a "Locked · {name}" success badge plus a
                separate "Flow id: …" line below it, implying the same kind of
                pin/lock this unit removed everywhere else. The Gong bind
                itself is still one-way (no unset, no free-text override —
                unrelated, untouched machinery), so this is now a single plain
                line naming the bound flow, falling back to a generic phrase
                (plan 2026-09-28-008 U4, R20/F9g — never the raw id) when the
                catalog lookup misses.
              */}
              {isGong && lockedFlowId && (
                <div className="flex items-center gap-1.5">
                  <Text size="xs" variant="tertiary">
                    Gong flow: {gongFlowDisplayName(lockedFlowName)}
                  </Text>
                  {/* C2 — the entry point to D4's swap (Batch 6); today opens
                      the plain mapping drawer (D2's Gong tab isn't built yet). */}
                  <Button
                    variant="inline-text"
                    size="xs"
                    onClick={() => openMappingModal('gong_engage')}
                  >
                    Change flow
                  </Button>
                </div>
              )}
              {/*
                Codex finding 2 fix — an unbound Gong sequence with EXISTING
                (non-empty) steps had NO way to bind a flow at all: the
                `steps.length === 0` empty-state doorway (below, in the card
                body) is the only place `openMappingModal('gong_engage')` was
                reachable for the unbound case, and it never renders once the
                sequence already carries a step (a legal state — a
                placeholder/manually-typed step on a sequence never imported
                through Gong). This mirrors the bound line just above it
                (same inline-text button, same `openMappingModal('gong_engage')`
                call) so the header always has a Gong-flow affordance
                regardless of step count.
              */}
              {isGong && !lockedFlowId && renderedVersion && renderedVersion.steps.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <Text size="xs" variant="tertiary">
                    No Gong flow chosen yet
                  </Text>
                  <Button
                    variant="inline-text"
                    size="xs"
                    onClick={() => openMappingModal('gong_engage')}
                  >
                    Choose a Gong flow
                  </Button>
                </div>
              )}
              {/*
                playbooks-inflection-wiring (2026-09-17) — the flow-ID fix for
                every NON-Gong channel: `sequence-readiness.ts` requires a flow
                on every channel, and there was previously no write path a
                non-Gong sequence could ever satisfy it through (see TL;DR item
                1 of that plan). Gong keeps the two doorways above, untouched.
              */}
              {!isGong && sequenceSummary && (
                <div className="flex items-center gap-1.5">
                  <Text size="xs" variant="tertiary">
                    {`${channelLabel(mapping.channel)} flow ID: ${lockedFlowId ?? 'Not set'}`}
                  </Text>
                  <SequenceFlowIdPopover
                    sequenceId={mapping.sequenceId}
                    channel={mapping.channel}
                    currentFlowId={lockedFlowId}
                    usageCount={sequenceSummary.usageCount}
                    variant="inline-text"
                    size="xs"
                    playId={playId}
                    playbookId={playbookId}
                    usageOrigin={origin}
                    sequenceName={sequenceSummary.name}
                    owningPlayId={sequenceSummary.owningPlayId}
                    archivedInLibrary={isArchivedInLibrary(sequenceSummary)}
                  />
                </div>
              )}
              {/*
                U9 (SN1) / Decision 3 — this label is READ-ONLY; the control
                that WRITES the sender lives in the header cluster to the
                right (R5-2, below) and on Config's row — never here, since a
                rung-level editor would be a per-rung override.

                R5-4 — reads `effectiveSender(sequenceSummary, mapping)`, the
                sequence's LIVE `default_sender`, not the rung's stale
                `mapping.sender` copy: round 4's Decision 11 (copy-on-map, no
                live inheritance) was the bug R5-4 fixed — a Config sender
                edit never reached an already-mapped rung. This label and the
                header popover's `currentSender` now read the same source, so
                they can never disagree.
              */}
              <Text size="xs" variant="tertiary">
                Sender: {senderLabel(effectiveSender(sequenceSummary, mapping))}
              </Text>
              {/* playbooks-inflection-wiring (2026-09-17) — read-only, same
                  reasoning as the Sender line just above: the CONTROL that
                  writes CC/Reply-to lives in the header cluster to the right. */}
              {mapping.channel === 'inflection' && sequenceSummary && (
                <Text size="xs" variant="tertiary">
                  {`CC: ${senderLabel(sequenceSummary.cc)} · Reply-to: ${senderLabel(sequenceSummary.replyTo)}`}
                </Text>
              )}
              {/*
                U1 (Decision 3, Deferred-to-Implementation default) — the
                Generate button's disabled-hint caption used to render
                directly under the button itself, inside the body's
                full-width block (deleted below). Now that Generate lives in
                the header cluster (small, icon+label, no room for a second
                line under it), the hint moves here, into this same flow-line
                column, so it stays visible next to the thing it explains.
              */}
              {editorMode === 'edit' && generateTrigger.hint && (
                <Text size="xs" variant="tertiary">
                  {generateTrigger.hint}
                </Text>
              )}
            </div>
            {/*
              U1 (R-L2, R-L3; Key Technical Decisions 2/3) — the top-right
              cluster (prototype `sequenceCard()` `:6570-6600`) used to be two
              always-visible buttons, "Change sequence" and "Remove sequence"
              (`R10`, PR C/U8). Round-4 QA (L2/L3) reads that as visual
              clutter competing with the card's actual hierarchy, so it's now
              purple "Generate with AI" (moved out of the body's full-width
              block below, which is deleted, not duplicated — exactly one
              "Generate with AI" button in this shell) followed by ONE
              `KebabMenu` (`sequence-card-menu.ts#getSequenceCardMenuItems`)
              holding Change / Rename / Remove(danger), in that order. No
              unset/edit/free-text affordance anywhere for the Gong flow lock
              itself (unrelated, untouched): there is no "change flow" kebab
              item, no clear icon — "Change flow" (above) opens the mapping
              drawer, it does not unset anything itself. Gong only — non-Gong
              channels have a free-text flow id popover (playbooks-inflection-
              wiring, 2026-09-17), rendered above in the flow-line column.

              R5-2 — `SequenceSenderPopover` renders FIRST in this cluster,
              before Generate with AI (Spencer's L4 ask). It is the SAME
              control Config's row renders, `variant="primary"` here per L4
              ("sender button sits next to Generate with AI, primary blue")
              instead of Config's `secondary`/`xs` — this is not a per-rung
              override, the popover still writes the SEQUENCE's
              `default_sender` (`components/SequenceSenderPopover.tsx`), and
              R5-4's live inheritance means every other rung mapped to this
              sequence sees the change immediately too. Only rendered while
              `sequenceSummary` has resolved — there is no sequence identity
              to write to yet if the library summary hasn't loaded.
            */}
            <div className="flex items-center gap-2">
              {sequenceSummary && (
                <SequenceSenderPopover
                  sequenceId={mapping.sequenceId}
                  currentSender={sequenceSummary.sender}
                  usageCount={sequenceSummary.usageCount}
                  variant="primary"
                  size="sm"
                  usageOrigin={origin}
                  sequenceName={sequenceSummary.name}
                  playId={playId}
                  playbookId={playbookId}
                  owningPlayId={sequenceSummary.owningPlayId}
                  archivedInLibrary={isArchivedInLibrary(sequenceSummary)}
                />
              )}
              {/* playbooks-inflection-wiring (2026-09-17) — same cluster,
                  right after the sender popover, Inflection-only. */}
              {mapping.channel === 'inflection' && sequenceSummary && (
                <SequenceInflectionPopover
                  sequenceId={mapping.sequenceId}
                  currentCc={sequenceSummary.cc}
                  currentReplyTo={sequenceSummary.replyTo}
                  usageCount={sequenceSummary.usageCount}
                  variant="primary"
                  size="sm"
                  playId={playId}
                  playbookId={playbookId}
                  usageOrigin={origin}
                  sequenceName={sequenceSummary.name}
                  owningPlayId={sequenceSummary.owningPlayId}
                  archivedInLibrary={isArchivedInLibrary(sequenceSummary)}
                />
              )}
              {editorMode === 'edit' && (
                <div className={GENERATE_AI_BUTTON_WRAPPER_CLASS}>
                  <Button
                    variant="primary"
                    size="sm"
                    data-row-control
                    disabled={generateTrigger.disabled}
                    onClick={() => {
                      // A rep can click Generate while the card itself is
                      // collapsed (Decision 3, QA doc question 6 default) —
                      // this both opens the card AND starts the session, so
                      // the drawer that follows isn't hidden behind a closed
                      // accordion.
                      setIsCardOpen(true);
                      openGenerateSession({
                        scope: rung.draftKey,
                        sequenceId: mapping.sequenceId,
                        surface: 'embedded',
                        seed: { campaignSfdcId },
                      });
                    }}
                  >
                    <Icon type={isGenerating ? ArrowsClockwise : MagicWand} />
                    {isGenerating ? 'Generating…' : 'Generate with AI'}
                  </Button>
                </div>
              )}
              <KebabMenu
                ariaLabel={`More actions for ${sequenceSummary?.name ?? SEQUENCE_UNAVAILABLE_NAME}`}
                items={getSequenceCardMenuItems({ canRename: sequenceSummary !== undefined }).map((menuItem) => ({
                  key: menuItem.action,
                  label: menuItem.label,
                  danger: menuItem.danger,
                  onSelect: () => {
                    if (menuItem.action === 'change') {
                      openMappingModal();
                    } else if (menuItem.action === 'rename') {
                      setRenameOpen(true);
                    } else {
                      // U5 (round-3-QA-fixes plan, Decision 6) — no confirm
                      // dialog here either: a null mapping is reversible via
                      // "Choose sequence", unlike the swap above, whose
                      // confirm exists because a re-point loses an
                      // association permanently. `handleRemoveMapping`'s own
                      // `isRemovingMapping` guard (declared with the state
                      // above) still protects a double-fire — `KebabMenuItem`
                      // has no `disabled` prop to mirror the old button's.
                      handleRemoveMapping();
                    }
                  },
                }))}
              />
            </div>
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="flex flex-col gap-3 p-4 border-border-primary">
          {/*
            U11 (item 20, R14, Key Decision D8, landmine 7) — the
            sequence-level Edit | Preview toggle (prototype `sequenceCard()`'s
            sub-header, `playbooks-prototype-v6.html:6602-6610`), reusing
            the same `@clay-run/glaze/segmented-control` this app already
            renders on `PlaybooksHome.tsx:329`. ONE mode governs the whole
            sequence's rendering below — every step's editor in `'edit'`, or
            every step's live-interpolated preview in `'preview'` — matching
            the prototype, not a per-step toggle.
          */}
          <div className="flex items-center gap-2 flex-wrap">
            <SegmentedControl size="sm" value={editorMode} onValueChange={handleModeChange}>
              <SegmentedControlOption value="edit">Edit</SegmentedControlOption>
              <SegmentedControlOption value="preview">Preview</SegmentedControlOption>
            </SegmentedControl>
            {/*
              Round-7B prototype placement (`:6609`): "Preview with N" sits
              right beside the toggle it drives. `N` = `rosterMembers.length`
              — the SAME roster (D10) the roster-wide token-gap check uses, so
              the count and the members table below agree.

              C3: this used to open `RungRosterDrawer` as a member picker. That
              drawer is deleted (decision #5) — the shared `MembersPanel` table
              in this step's own column IS the picker now, so this button's job
              is to scope that table to this rung and open Preview. Its label
              flips to "Hide preview" while previewing (decision #2), and in
              that state it is the affordance that unscopes the table.
            */}
            {editorMode === 'preview' ? (
              <Button variant="inline-text" size="xs" onClick={handleHidePreview}>
                <Icon type={EyeSlash} />
                Hide preview
              </Button>
            ) : (
              <Button variant="inline-text" size="xs" onClick={() => handleModeChange('preview')}>
                <Icon type={Users} />
                Preview with {rosterMembers.length}
              </Button>
            )}
            {/*
              Prototype `:6610`: the badge only shows in Preview mode, and
              only once there's a member to name it for (there may be none
              yet if the roster is empty).
            */}
            {editorMode === 'preview' && previewMember && (
              <Badge color="blue">
                <BadgeIcon type={Eye} />
                Previewing {previewMemberLabel(previewMember)}
              </Badge>
            )}
            {/* Preview renders unsaved copy, so it has to say so; the per-step
                editors carrying this badge are unmounted in this mode. */}
            {editorMode === 'preview' && hasUnsavedCopy && (
              <StatusBadge status="warning">Unsaved changes</StatusBadge>
            )}
          </div>
          {/*
            U10 (item 19, R13) — the roster-wide warning banner (prototype
            `:6645-6647`), rendered first (prototype round-5 item-12 note:
            "the doorway to members whose copy won't resolve ... is more
            load-bearing than any one step's editor below it"). Copy is
            EXACTLY the prototype's — `fmt(warnings.length) + ' unresolved
            merge fields'`, no pluralization branch, because the prototype's
            own `fmt` doesn't have one either.

            C3: no longer a button. It used to open `RungRosterDrawer` as a
            drill-down; that drawer is deleted (decision #5) and its per-member
            "Missing: {{Field}}" annotation is now a permanent column of the
            `MembersPanel` table sitting directly above this card — the rows
            are already on screen, so a door to them would lead nowhere.
          */}
          {rosterGaps.length > 0 && (
            <div className="flex items-center gap-1.5 self-start text-content-warning">
              <Icon type={Warning} />
              <Text size="sm" className="text-content-warning">
                {rosterGaps.length} unresolved merge fields
              </Text>
            </div>
          )}
          {isRenderedVersionLoading && (
            <div className="flex justify-center p-6">
              <LoadingSpinner size="md" />
            </div>
          )}
          {isRenderedVersionError && (
            <Alert status="error">Couldn&apos;t load this sequence. Please try again.</Alert>
          )}
          {!isRenderedVersionLoading && !isRenderedVersionError && renderedVersion === null && (
            <Text size="sm" variant="tertiary">
              This sequence has no content yet.
            </Text>
          )}
          {/*
            terra-qyt.17 (U12) — the step editors move into their own
            collapsible "Steps" accordion. Only rendered once a rendered
            version has actually loaded (mirrors the "no content yet" branch
            just above it) — a loading/error/head-less sequence has nothing
            to put in here. Glaze has no `Accordion` export; `CollapsibleCard`
            (already used for the outer rung card just above) IS the
            accordion.

            Round-4 QA plan U1 (R-L1, Decision 2) — DEFAULT CLOSED now
            (`stepsOpen`'s own doc above), and the whole header bar toggles
            it, not just Glaze's own caret.

            Round-4 QA codex-review fix (batch 1, finding 3) — Glaze's
            `CollapsibleCardHeader` owns its own surrounding `p-4`/gap/caret
            internally (`collapsible-card.tsx`) and forwards no `onClick`/
            `className` of its own; the handler used to sit on an INNER flex
            child that only spans the content box, not that padding, so
            clicking the header's padding did nothing. The click/keydown
            handlers now sit on a wrapper spanning the WHOLE header
            (including the padding and the caret button) instead —
            `isInteractiveRowTarget` still suppresses the wrapper's own
            toggle for a click that lands on the caret's real `<button>`, so
            base-ui's own `Collapsible.Trigger` click handling is the only
            thing that fires for that one target, exactly once either way.
          */}
          {!isRenderedVersionLoading && !isRenderedVersionError && renderedVersion !== null && (
            <CollapsibleCard open={stepsOpen} onOpenChange={setStepsOpen}>
              <div
                className="cursor-pointer"
                tabIndex={0}
                onClick={(e) => {
                  if (isInteractiveRowTarget(e.target)) return;
                  setStepsOpen((v) => !v);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  if (isInteractiveRowTarget(e.target)) return;
                  e.preventDefault();
                  setStepsOpen((v) => !v);
                }}
              >
                <CollapsibleCardHeader>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Text weight="semibold">Steps</Text>
                    <Badge>{renderedVersion.steps.length}</Badge>
                  </div>
                </CollapsibleCardHeader>
              </div>
              <CollapsibleCardBody className="flex flex-col gap-3 p-4">
                {/*
                  C1/C2 — an empty-steps Gong sequence gets a Gong-specific
                  state instead of the generic "No steps" text: unbound gets
                  the "Choose a Gong flow" doorway (where the generic
                  empty-steps text used to render), bound gets the "Load
                  steps from Gong" retry (nothing to lose — the sequence has
                  zero steps by construction here, so no confirm). Both cases
                  fall through to isGong===false unaffected.
                */}
                {renderedVersion.steps.length === 0 && (
                  <div className="flex flex-col items-start gap-2">
                    {isGong && !lockedFlowId ? (
                      <>
                        <Text size="sm" variant="tertiary">
                          This Gong sequence needs a flow before it has any copy.
                        </Text>
                        <Button variant="secondary" size="sm" onClick={() => openMappingModal('gong_engage')}>
                          Choose a Gong flow
                        </Button>
                      </>
                    ) : (
                      <>
                        <Text size="sm" variant="tertiary">
                          No steps on this sequence yet.
                        </Text>
                        {isGong && (
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={isImportingSteps}
                            onClick={() => void handleLoadStepsFromGong()}
                          >
                            Load steps from Gong
                          </Button>
                        )}
                        {/*
                          QA round 8, item 6 — a legacy (pre-this-round)
                          non-Gong sequence with zero steps is otherwise a
                          dead end: the play flow never adds steps and
                          "Generate with AI" refuses to run on zero steps
                          (`copy-generation.ts`). This is the ONLY doorway
                          out of that state for an existing sequence; a
                          NEW sequence is now born with one step
                          (`seedStepsForChannel`) and never reaches here.
                        */}
                        {!isGong && (
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={isAddingFirstStep}
                            onClick={() => void handleAddFirstStep()}
                          >
                            Add first step
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {/*
                  U11 — Edit mode renders the real editors (drafts persist in
                  `sequence-copy-draft-store.ts`, keyed by rung+sequence+position
                  since D1's scoping fix, so unmounting these `SequenceStepEditor`s
                  while Preview mode is active
                  does NOT lose any in-progress typing — U9's guarantee, unaffected
                  by this toggle). Preview mode renders the live-interpolated
                  per-step preview instead, evolving `SamplePreviewCard` (below)
                  rather than duplicating its rendering.

                  U7b — the real per-step review renders INLINE below, as each
                  `SequenceStepEditor` swaps into `StepReviewCard` mode via its
                  own `review` prop (U7a) — see that prop's construction below
                  the step-editor map. The drawer mounted past this card
                  (`isSessionOpenHere`) is the session's stage machine
                  (configure form, Generate button, review tally/actions) — it
                  does not render the per-step cards itself; see
                  `GenerateWithAIDrawer`'s own doc.
                */}
                {editorMode === 'edit' ? (
                  stepsInGongOrder.map((step) => {
                    // U7b — this step's staged review (U7a's `StepReview`), or
                    // `undefined` for ordinary hand-edit mode. Only a `'ready'`
                    // entry has per-step reviews at all — a `'pending'` generation
                    // has nothing for `SequenceStepEditor` to show yet beyond the
                    // drawer's own "Writing step N of M…" progress line.
                    const stepReview =
                      generation?.status === 'ready' ? generation.steps[step.position] : undefined;
                    return (
                      <SequenceStepEditor
                        key={step.position}
                        step={step}
                        // QA round 7, U5 (item 7) — the position-sorted list's
                        // 1-based index, matching `toOverrideSteps`/`DispatchInspector`.
                        ordinal={stepOrdinal(stepsInGongOrder, step.position)}
                        sequenceId={mapping.sequenceId}
                        // D1 shared-edit-leak fix — this rung's own `draftKey`, so a
                        // draft belongs to (rung, sequence, step) instead of just
                        // (sequence, step). See `sequence-copy-draft-store.ts`.
                        draftScope={rung.draftKey}
                        renderedVersionId={renderedVersion.versionId}
                        // Gong 400s the WHOLE enrollment over a subject on a
                        // reply step, so the field is not offered for one.
                        // Ordinal-against-the-flow, the send's own rule — see
                        // `gongReplyOrders` above.
                        isReplyStep={isReplyAtOrdinal(stepsInGongOrder, step.position, gongReplyOrders)}
                        onSave={handleSaveStep}
                        // F2 (Batch 3, decision 10), hybrid rule (plan A2,
                        // `2026-09-03-001-launcher-v2-round2-followup-plan.md`) —
                        // `bodyFormat` is a PER-STEP decision: `gongStepBodyFormat`
                        // treats email-family steps (`step.gongStepDescriptor.step_type`)
                        // as unconditionally rich, and promotes any other step type
                        // to rich only when its own CURRENT `body` content is shaped
                        // like Gong's real HTML envelope. Non-Gong channels, and any
                        // non-email step whose body isn't real HTML (including no
                        // body at all), fall through to plain.
                        bodyFormat={bodyFormatByPosition.get(step.position) ?? 'plain'}
                        review={
                          stepReview
                            ? {
                                entry: stepReview,
                                stagedGeneration,
                                onEdit: (patch) =>
                                  editStagedStep(rung.draftKey, mapping.sequenceId, step.position, patch),
                                onDecide: (decision) =>
                                  decideStagedStep(rung.draftKey, mapping.sequenceId, step.position, decision),
                                // Upstream Discovery (U7a) — this component has no
                                // member/campaign/sender context of its own; the
                                // caller computes gaps against the GENERATED
                                // (edited ?? staged) copy, mirroring `rosterGaps`
                                // above.
                                tokenGaps: stepTokenGaps(
                                  {
                                    subject: stepReview.edited?.subject ?? stepReview.staged.subject,
                                    body: stepReview.edited?.body ?? stepReview.staged.body,
                                  },
                                  sampleMember,
                                  campaign,
                                  // R5-4 — see `rosterGaps`'s own comment above.
                                  effectiveSender(sequenceSummary, mapping),
                                ),
                              }
                            : undefined
                        }
                        // U9b (R10) — the token picker's live-preview context:
                        // this shell's own `sampleMember`/`campaign` (same
                        // values `tokenGaps` above already reads) plus the
                        // sequence's LIVE sender (R5-4), never the rung's stale
                        // `mapping.sender` copy.
                        sampleMember={sampleMember}
                        campaign={campaign}
                        sender={effectiveSender(sequenceSummary, mapping)}
                      />
                    );
                  })
                ) : (
                  <SamplePreviewCard
                    // Prod QA, 2026-09-16 (Fix 3) — the LIVE steps (saved head
                    // + this rung's unsaved drafts). Same array the roster-gap
                    // banner above counts, so the two can never disagree.
                    steps={liveSteps}
                    previewMember={previewMember}
                    // R5-4 — the sequence's live sender, not the rung's stale copy.
                    sender={effectiveSender(sequenceSummary, mapping)}
                    campaign={campaign}
                    // D1 (live-QA round 2, Batch 5) — same per-step gate the editor
                    // uses (`gongStepBodyFormat`, now content-based per plan A2):
                    // only steps whose OWN CURRENT body looks like Gong's real HTML
                    // envelope get the rich HTML render, never a channel-wide
                    // assumption and never a `step_type` lookup.
                    isGong={isGong}
                    // U2b/Codex F3 — the bound flow's LIVE reply-step ordinals
                    // (same hook + numbering the editor above uses via
                    // `isReplyAtOrdinal`), so the preview drops a subject at
                    // exactly the ordinals the send drops it at — see
                    // `SamplePreviewCard`'s own doc for why this must run
                    // before the blankness check too.
                    replyStepOrders={gongReplyOrders}
                  />
                )}
              </CollapsibleCardBody>
            </CollapsibleCard>
          )}
        </CollapsibleCardBody>
      </CollapsibleCard>

      {/*
        C2 — no standalone picker mount here any more. `LauncherGongFlowBrowse`
        (round-3b QA fixes G3) has no host on this card at all — its only
        host is `SequenceMappingDrawer`'s Gong tab (Unit D2, Batch 6).
        `mappingModal` below is the card's one and only Gong entry point now.
      */}
      {mappingModal}
      {/*
        C3 (decision #5): no drawer here any more. `RungRosterDrawer` hosted
        two things — the unresolved-merge-fields drill-down and the "Preview
        with N" member picker — and the shared `MembersPanel` table above this
        card now does both jobs inline (its "Missing fields" column and its
        clickable rows respectively). The component is deleted, not replaced.
      */}
      {/*
        U7b — the "Generate with AI" drawer, mounted ONLY while a session is
        open for THIS exact (rung, sequence) pair (`selectIsSessionOpenFor`) —
        matching the precedent every other rung-scoped drawer in this file
        already sets (`mappingModal`, `SequenceMappingDrawer`). `campaign` may
        still be `null` here while `useLauncherCampaign` is loading; the
        drawer's own `GenerateConfigureForm` tolerates that exactly like the
        old direct-call `handleGenerate` did — see this file's own doc for
        `campaignSfdcId` vs. `campaign`.
      */}
      {isSessionOpenHere && (
        <GenerateWithAIDrawer
          scope={rung.draftKey}
          sequenceId={mapping.sequenceId}
          surface="embedded"
          campaign={
            campaign
              ? {
                  sfdcId: campaignSfdcId,
                  name: campaign.name,
                  type: campaign.type ?? null,
                  dates:
                    campaign.start_date && campaign.end_date
                      ? `${campaign.start_date} – ${campaign.end_date}`
                      : campaign.event_date ?? null,
                }
              : null
          }
          rungConditions={rungConditions}
          headSteps={renderedVersion?.steps ?? []}
          headVersionId={renderedVersion?.versionId ?? null}
          hostCampaignSfdcId={campaignSfdcId === '' ? null : campaignSfdcId}
          onClose={closeGenerateSession}
          sequenceName={sequenceSummary?.name ?? SEQUENCE_UNAVAILABLE_NAME}
          usageCount={sequenceSummary?.usageCount ?? 0}
          // U1 (R-L1, Decision 2) — a successful apply-path save reopens the
          // Steps accordion so the generated result is visible immediately,
          // preserving round-3 U12's original intent without making it the
          // DEFAULT open state (see `stepsOpen`'s own doc above). Ordinary
          // hand-edit saves (`handleSaveStep`, which also calls `saveWithGate`
          // directly) do NOT go through this wrapper — Steps is already open
          // whenever a rep is editing a step.
          onApply={(steps, generationInputs) =>
            saveWithGate(steps, generationInputs).then((response) => {
              setStepsOpen(true);
              return response;
            })
          }
        />
      )}
      {/*
        Round-4 QA codex-review fix (batch 1, finding 1) — gated on
        `sequenceSummary` (not just `renameOpen`): the kebab's Rename item is
        already hidden while `sequenceSummary` is undefined
        (`getSequenceCardMenuItems({ canRename: ... })` above), so this is
        belt-and-suspenders against a library query that resolves and THEN
        errors/empties between the click and this render — never fall back
        to `mapping.sequenceId` as a NAME or to `0` as a usage count.
      */}
      {renameOpen && sequenceSummary && (
        <PushDrawer title={`Rename ${sequenceSummary.name}`} onClose={() => setRenameOpen(false)}>
          <NameDrawerField
            key={mapping.sequenceId}
            // Plan 2026-09-29-001 U2 (R14, B5) — reopening the drawer while a
            // Save panel for THIS group's rename is still pending seeds the
            // field with what the rep typed, not the saved name they had
            // already decided to change (`settings-draft-restore.ts`).
            initialName={pendingRenameName ?? sequenceSummary.name}
            saving={savingRename}
            onSave={handleRenameSave}
            onCancel={() => setRenameOpen(false)}
            // Plan 2026-09-28-006 U4 — doorway only; in a play the gate speaks.
            warning={playId === undefined ? renameWarningText(sequenceSummary.usageCount) : null}
          />
        </PushDrawer>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Live per-step preview (U6/R3, evolved by U11/R14, C2's segment rendering) —
// reuses `copy-token-flags.ts`'s `previewStepCopySegments`, which itself
// reuses v1's `interpolateTemplateSegments` verbatim (team convention: reuse,
// don't re-derive). Read-only by design: this previews the SAVED head steps,
// never an editor (matching `SequenceStepEditor`'s own live-draft-vs-saved-head
// split — Preview mode shows what a real send looks like today, not this
// session's unsaved typing). U11 changed WHEN this renders (only in Preview
// mode, inside the sequence card, instead of an always-on card below it) and
// WHO it renders for (`previewMember` — the rep's explicit "Preview with N"
// pick, or the `sampleMember` fallback — instead of always the fixed sample).
//
// C2 (plan `2026-09-01-001`) changed HOW the interpolated copy renders: a
// known-but-empty token (a real field, currently blank for this member) and
// an unrecognized token both render as a visible, warning-styled chip
// carrying the raw `{{Token}}` text — matching the prototype's `interpolate()`
// (`playbooks-prototype-v6.html:3571-3577`, CSS `.token-empty` ~2139) —
// instead of silently collapsing to blank text. A resolved token gets the
// lighter `.token-filled` treatment for visual consistency (optional per the
// plan, adopted here). This rendering is deliberately self-contained inside
// `SamplePreviewCard` so C3 (which re-wires what drives `previewMember`) can
// compose on top without touching the rendering itself.
// ─────────────────────────────────────────────────────────────────────────────

/** One template segment, styled per its `kind` — the C2 empty/unknown-token visual treatment. */
function CopySegments({ segments }: { segments: readonly TemplateSegment[] }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.kind === 'text') return segment.text;
        if (segment.kind === 'filled') {
          return (
            <span
              key={index}
              className="rounded-[0.125rem] bg-bg-emphasis px-[0.1875rem] text-content-primary"
            >
              {segment.text}
            </span>
          );
        }
        // `deferred` (2026-09-16) — a Clay-owned token. Its text is Terra's
        // BEST GUESS when it has one (the sender name/link) and the braced
        // token otherwise (`{{Company Name}}`), so the rep reads a plausible
        // sentence instead of a placeholder. It gets its OWN treatment, not
        // the `filled` one, because the guess is knowingly not what the
        // recipient will read: Clay resolves the sender and the standardized
        // company name after routing, and the payload carries the literal
        // token. Info-toned rather than warning-toned — nothing here is
        // wrong, unlike `empty`/`unknown` below — with the token named in the
        // `title` so hovering says which one it is and who fills it.
        if (segment.kind === 'deferred') {
          return (
            <span
              key={index}
              className="rounded-[0.125rem] border border-dashed border-border-selected bg-bg-blue-light px-[0.1875rem] text-content-action"
              title={`{{${segment.token}}} — ${CLAY_OWNED_TOKEN_CAPTION}`}
              aria-label={`{{${segment.token}}} — ${CLAY_OWNED_TOKEN_CAPTION}`}
            >
              {segment.text}
            </span>
          );
        }
        // `empty` (a known field, currently blank for this member) and
        // `unknown` (not in the merge-token vocabulary at all) get the same
        // warning treatment — both are "this won't render real content",
        // just for different reasons. Exact prototype spec: warning
        // background/text color, 2px border radius, `0 .1875rem` padding,
        // monospace font, `.75rem` font size.
        return (
          <span
            key={index}
            className="rounded-[0.125rem] bg-bg-warning px-[0.1875rem] font-mono text-[0.75rem] text-content-warning"
          >
            {segment.text}
          </span>
        );
      })}
    </>
  );
}

function SamplePreviewCard({
  steps,
  previewMember,
  sender,
  campaign,
  isGong,
  replyStepOrders,
}: {
  steps: readonly SequenceStep[] | undefined;
  previewMember: CampaignMember | null;
  sender: string;
  /**
   * U10 fix — the real campaign (or `null` while `useLauncherCampaign` is
   * still loading), passed through to `previewStepCopy` so campaign-scoped
   * tokens (`{{Event Name}}`/`{{Event Date}}`/`{{Campaign Type}}`) interpolate
   * with real values instead of always resolving to the literal `{{Field}}`
   * placeholder.
   */
  campaign: Campaign | null;
  /**
   * D1 (live-QA round 2, Batch 5) — whether this rung's sequence is on the
   * Gong channel at all. Gates the per-step `gongStepBodyFormat` check the
   * same way the editor does (`isGong ? gongStepBodyFormat(...) : 'plain'`):
   * a step's own CURRENT body content (plan A2) is the real per-step signal,
   * this is only the cheap short-circuit for every non-Gong sequence.
   */
  isGong: boolean;
  /**
   * Codex F3 fix — the bound flow's LIVE reply-step ordinals
   * (`use-gong-reply-step-orders.ts`), the exact set `toOverrideSteps`
   * (the send) and `SequenceStepEditor` (the editor) already key off via
   * {@link isReplyAtOrdinal}. Before this prop existed, this card had no
   * way to know a step was a reply and would preview a subject the send
   * never transmits (a real Tier 1/2 fixture ordinal previewed "Re: Quick
   * question" while the wire subject was empty) — and, worse, would judge a
   * reply-ordinal non-email step's blankness on its RAW (about-to-be-dropped)
   * subject instead of the effective one, previewing a script where the send
   * actually leaves the slot blank. Empty set = "shape unknown", matching the
   * hook's own default — every subject stays in place.
   */
  replyStepOrders: ReadonlySet<number>;
}) {
  // QA round 7, U5 (item 7) — `steps` (`liveSteps`) is not guaranteed
  // position-sorted; the sorted list is both the iteration order (so display
  // order equals ordinal order) and the reference `stepOrdinal` numbers against.
  const sortedSteps = useMemo(() => sortStepsByPosition(steps ?? []), [steps]);
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border-secondary bg-bg-primary p-4">
      <Text size="sm" weight="semibold">
        Sample preview
      </Text>
      {!previewMember && (
        <Text size="xs" variant="tertiary">
          No campaign members to preview against yet.
        </Text>
      )}
      {previewMember && (
        <>
          <Text size="xs" variant="tertiary">
            Previewing as {previewMemberLabel(previewMember)}
          </Text>
          {sortedSteps.length === 0 && (
            <Text size="xs" variant="tertiary">
              No steps to preview yet.
            </Text>
          )}
          {sortedSteps.map((step) => {
            // U2b (plan `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`,
            // decisions 1/2/3/8) — a BLANK non-email Gong step (Call /
            // LinkedIn Connect / LinkedIn Message / Custom) has nothing to
            // preview: same card shell, its own `gongStepTypeLabel` chip, and
            // the shared blank sentence — no Subject line, no body. A
            // non-email step that CARRIES a script instead falls through to
            // the normal render below, exactly like an email step: Terra
            // resolves and sends it the same way
            // (`send-payload.ts#toOverrideSteps`), so the preview must show
            // the same interpolated rendering the send will produce, not the
            // raw unresolved script.
            const gongStepType = gongStepTypeLabel(step.gongStepDescriptor);
            // Codex F3 fix — computed BEFORE the blankness check AND before
            // rendering: at a reply ordinal Gong drops the subject
            // regardless of step type (the send's own rule,
            // `send-payload.ts#toOverrideSteps`), so blankness must be
            // judged on the EFFECTIVE subject, not the raw stored one — the
            // same fix that fixture's send-side test needed (Codex F1). A
            // reply-ordinal non-email step with an imported title and a
            // cleared body now previews as the blank caption, matching what
            // the send actually transmits, instead of previewing a title the
            // wire never carries.
            const isReplyStep = isReplyAtOrdinal(sortedSteps, step.position, replyStepOrders);
            if (
              !isGongEmailStep(step) &&
              isContentlessGongStep({ subject: isReplyStep ? '' : step.subject, body: step.body })
            ) {
              return (
                <div
                  key={step.position}
                  className="flex flex-col gap-1 rounded-md border border-border-secondary p-3"
                >
                  <div className="flex items-center gap-2">
                    <Text size="xs" weight="medium">
                      Step {stepOrdinal(sortedSteps, step.position)}
                    </Text>
                    {gongStepType && <Badge>{gongStepType}</Badge>}
                  </div>
                  <Text size="xs" variant="tertiary">
                    {GONG_BLANK_STEP_TEXT}
                  </Text>
                </div>
              );
            }
            const preview = previewStepCopySegments(step, previewMember, campaign, sender);
            if (!preview) return null;
            // D1 — PER-STEP decision, hybrid rule (plan A2): an email-family
            // step (`step.gongStepDescriptor.step_type`) always gets the rich
            // HTML render; any other step type gets it only when its OWN
            // CURRENT `body` is shaped like Gong's real HTML envelope.
            // Interpolation has already run above (`previewStepCopySegments`)
            // — joining the segments' `text` fields reconstructs the exact
            // interpolated string (each segment's `text` is either literal
            // template text, a resolved value, or the raw `{{Token}}`
            // placeholder for an empty/unknown token), so an unresolved token
            // still shows its literal text inside the HTML body — only the
            // chip SPAN styling is skipped, matching Spencer's "process them
            // correctly" confirmation. Subjects always keep the chip
            // treatment via `CopySegments`, regardless of format.
            const bodyFormat = isGong ? gongStepBodyFormat(step.body, step.gongStepDescriptor) : 'plain';
            // U2b — the joined subject text decides whether the "Subject:"
            // line renders at all for a NON-reply step (a non-email step's
            // subject is simply optional, decision 4). `preview.subject` is
            // the diagnostic segment view, not the final copy, but every
            // segment kind (`text`/`filled`/`empty`/`deferred`/`unknown`)
            // carries a `text` field, so joining it is a safe non-empty check
            // either way.
            const subjectText = preview.subject.map((segment) => segment.text).join('');
            return (
              <div
                key={step.position}
                className="flex flex-col gap-1 rounded-md border border-border-secondary p-3"
              >
                {/*
                  U2b — a `<span>` row, not a `<div>`: pre-existing tests
                  locate this card via `.closest('div')` from the "Step N"
                  text node, which must still resolve to THIS outer card, not
                  an intermediate wrapper.
                */}
                <span className="flex items-center gap-2">
                  <Text size="xs" weight="medium">
                    Step {stepOrdinal(sortedSteps, step.position)}
                  </Text>
                  {gongStepType && <Badge>{gongStepType}</Badge>}
                </span>
                {isReplyStep ? (
                  // Codex F3 fix — a reply ordinal never carries a subject on
                  // the wire (the send drops it for every step type), so the
                  // preview shows the same hint the editor shows in its place
                  // rather than a subject the recipient will never see.
                  <Text size="xs" variant="tertiary">
                    {REPLY_STEP_NO_SUBJECT_TEXT}
                  </Text>
                ) : (
                  subjectText && (
                    <Text size="xs" variant="tertiary">
                      Subject: <CopySegments segments={preview.subject} />
                    </Text>
                  )
                )}
                {bodyFormat === 'rich' ? (
                  <SanitizedHtml
                    // Codex adversarial-review finding 1 (Batch 5 fix-up) —
                    // only a `filled` segment (a resolved CRM/roster value)
                    // is untrusted; the rep's own authored `text` markup and
                    // the literal `{{Token}}` placeholder text of `empty`/
                    // `unknown` segments must NOT be escaped. Escaping here,
                    // ahead of `SanitizedHtml`'s DOMPurify pass, is what
                    // actually closes the markup-injection gap — DOMPurify's
                    // default policy retains ordinary markup (e.g. `<img>`),
                    // so an unescaped filled value would otherwise survive
                    // sanitization intact.
                    html={preview.body
                      .map((segment) => (segment.kind === 'filled' ? escapeHtml(segment.text) : segment.text))
                      .join('')}
                    className="text-xs whitespace-pre-wrap"
                  />
                ) : (
                  <Text size="xs" className="whitespace-pre-wrap">
                    <CopySegments segments={preview.body} />
                  </Text>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step
// ─────────────────────────────────────────────────────────────────────────────

export interface SequencesStepProps {
  campaignSfdcId: string;
  /**
   * Optional as of terra-qyt.14 (U9) — `undefined` in playbook mode, where
   * `playbookId` below is the real identity and no `getPlays` lookup runs.
   */
  playId?: string;
  /**
   * terra-qyt.14 (U9) — playbook mode's identity, supplied directly by
   * `PlayFlowShell` — see `QualifyStepProps.playbookId`'s identical doc.
   */
  playbookId?: string;
  onGoToReview: () => void;
  /**
   * `MembersPanel`, built once by `PlayFlowShell` and handed down as a slot
   * (PR B follow-up U5/R5) — `null` when there's no campaignSfdcId (shell
   * renders its own error band in that case instead).
   */
  membersPanel: ReactNode;
  /**
   * Round-3c QA fixes, H2 — the `?rung=` param off a "Where is this used"
   * deep link (`UsagePlacesList`, H1): a rung id to preselect on mount (or
   * when this changes to a genuinely different id), mapped to the matching
   * `DraftRung.draftKey` over `railRungs`. `null`/unknown/omitted → no
   * preselect, the existing `selectRung` fallback chain decides as it
   * always has. Optional (rather than required) so the many pre-H2 test
   * call sites that render `SequencesStep` directly keep compiling
   * unmodified.
   */
  preselectRungId?: string | null;
}

export function SequencesStep({
  campaignSfdcId,
  playId,
  playbookId,
  onGoToReview,
  membersPanel,
  preselectRungId = null,
}: SequencesStepProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  /**
   * D3 — read/write access to this route's OWN `?rung=` param, independent
   * of `PlayFlowShell`'s `preselectRungId` prop (that prop is a one-way,
   * READ-only preselect; this step is the one that also WRITES it, both on
   * a plain rail click and when a usage-sidebar decision forces a
   * different rung's selection — see the effect below). Calling
   * `useSearchParams()` here works because it reads the SAME v6 Router
   * location `PlayFlowShell` does, not a private copy.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * The selected rung's `draftKey` — never its position (a reorder rewrites
   * those, so the selection would silently jump to a different rung) and no
   * longer its `PlaybookRung.id` either, because the rail now renders STAGED
   * rungs and a rung added this session has no server id yet.
   * `rungDraftKey(rung.id)` for a persisted rung, `new:N` for an unsaved one —
   * both stable for the rung's whole draft lifetime (`playbook-draft.ts`).
   */
  const [selectedDraftKey, setSelectedDraftKey] = useState<string | null>(null);

  const libraryQuery = useQuery({
    queryKey: libraryQueryKey(playId),
    queryFn: () => getLibrary(apiFetch, { playId }),
  });

  // terra-qyt.14 (U9) — SKIPPED (not merely disabled) in playbook mode; see
  // `QualifyStep`'s identical query for the full doc.
  const playsQuery = useQuery({
    queryKey: playsQueryKey(campaignSfdcId),
    queryFn: () => getPlays(apiFetch, { campaignSfdcId }),
    enabled: !!campaignSfdcId && playbookId === undefined,
  });

  const play = playsQuery.data?.plays.find((p) => p.id === playId) ?? null;
  // terra-qyt.14 (U9) — one expression, not a new hook (see `QualifyStep`).
  const resolvedPlaybookId = playbookId ?? play?.playbookId;

  const playbookQuery = useQuery({
    queryKey: playbookDetailQueryKey(resolvedPlaybookId ?? '', playId),
    queryFn: () => getPlaybook(apiFetch, resolvedPlaybookId!, { playId }),
    enabled: !!resolvedPlaybookId,
  });

  /**
   * The staged playbook draft (PR C, U3B/U3C — see `playbook-draft-store.ts`).
   * Seeded here too, idempotently, the same as `QualifyStep`/`ReviewStep`:
   * this is where the sender and (as of the PR C hardening pass) the SEQUENCE
   * mapping modals write, and this step renders from it.
   */
  const draft = usePlaybookDraftStore(selectPlaybookDraft);
  useEffect(() => {
    // `playbookQuery.data !== undefined` (not just `play?.playbookId`) is the
    // real gate — Codex-hardening fix found while building U5. `playsQuery`
    // and `playbookQuery` are SEQUENTIALLY DEPENDENT (`playbookQuery` only
    // enables once `play.playbookId` exists), so there is a render where
    // `play?.playbookId` is already truthy but `playbookQuery.data` has not
    // arrived yet. Falling back to `?? null` in that window used to seed an
    // EMPTY draft (`head: null` reads as "no version yet") and mark this
    // `playbookId` as already-seeded — `ensurePlaybookDraft`'s no-op guard
    // then permanently ignored the real head that arrived a tick later, so
    // every rung/sender edit silently landed on an empty draft and never
    // reached `assembleDraftRungs`. Gating on the query object itself (not a
    // derived `?? null`) tells "not loaded yet" and "loaded, genuinely no
    // version" apart.
    if (resolvedPlaybookId && playbookQuery.data !== undefined) {
      // Plan 002, U6 (Decision 16) — in a play the draft writes the play's ladder.
      ensurePlaybookDraft(resolvedPlaybookId, playbookQuery.data.head, playId ?? null);
    }
  }, [resolvedPlaybookId, playbookQuery.data]);

  /**
   * The rail renders the STAGED ladder, not `head.rungs` (Codex
   * adversarial-review fix, PR C). Two bugs died with that switch:
   *
   *  - a rung the rep added on Qualify (no server id yet) was ABSENT here, so
   *    there was no way to route it to a sequence before saving — and no way at
   *    all, since nothing else in the app called
   *    `setPlaybookDraftRungMapping`;
   *  - a rung the rep had staged for REMOVAL was still listed and editable, so
   *    they could spend time mapping a rung the next save deletes.
   *
   * `draft` is `null` only in the window before the seeding effect above has
   * run once; falling back to `seedDraftFromHead(head).rungs` gives that window
   * exactly the ladder the draft is about to hold (rather than a one-frame
   * empty flash), using the same conversion so `draftKey`s match.
   */
  const railRungs: readonly DraftRung[] =
    draft?.rungs ?? seedDraftFromHead(playbookQuery.data?.head ?? null).rungs;
  /**
   * QA follow-up U7 (item 14, R8) — auto-select on fresh load rather than
   * defaulting to the dead "Select a segmentation…" empty state. Fallback
   * chain (prototype `:3393`): explicit selection → first mapped rung →
   * first rung → `null` only when the rail itself is empty. See
   * `rung-selection.ts` for the pure derivation and its test coverage.
   */
  const selectedRung = selectRung(railRungs, selectedDraftKey);

  /**
   * Round-3c QA fixes, H2 — preselect from the `?rung=` param by mapping the
   * rung id onto its `draftKey` over `railRungs`, ONCE per distinct
   * `preselectRungId` value.
   *
   * QA round 8 Codex fixes, F9 (plan U6) — `?rung=` IS THIS EDITOR'S
   * SELECTION SOURCE OF TRUTH. It used to be one of three competing writers,
   * with a `userClickedRungRef` flag that made a rail click permanently
   * silence the param for the rest of the mount ("a later rail click always
   * wins"). That flag is DELETED: the rail's own `onSelect` (below) already
   * writes `?rung=` on every click, so the URL is a faithful record of the
   * selection and "a later click wins" now falls out of plain equality
   * against {@link appliedRungParamRef} rather than a latch. The bug the
   * latch caused: `UsageSidebar`'s "Back to editing" only NAVIGATES
   * (`usage-sidebar-logic.ts#originUrl`), so after a rail click it changed
   * the address bar and hid its own button while leaving the OTHER rung on
   * screen — against the spec's "returns to the exact origin. Same play,
   * same segment" (`docs/playbooks-usage-sidebar-spec.md`).
   *
   * `appliedRungParamRef` — the `preselectRungId` value already applied, so
   * re-renders where `railRungs` changes but the param has not do not
   * re-fire `setSelectedDraftKey`. An unknown/not-yet-loaded rung id is
   * silently ignored and deliberately NOT marked applied: `railRungs` may
   * still be loading the first time this runs, so "no match yet" retries on
   * the next `railRungs` change (and simply never succeeds for a truly
   * unknown id — harmless, `railRungs` stabilizes and stops changing).
   *
   * The six interleavings this has to survive, in order of how the writers
   * can actually interleave:
   *
   *  1. Deep link `?rung=A` on mount → A ≠ applied `null`, matched → A
   *     selected, applied `A`.
   *  2. Rail click B → `onSelect` sets the selection AND `?rung=B` → this
   *     effect sees B ≠ applied A, so it calls `setSelectedDraftKey(B)` with
   *     the value already there (idempotent, React bails on the re-render)
   *     → applied `B`.
   *  3. A save landing changes `railRungs` (a brand-new array) with the param
   *     unchanged → B === applied B → no-op. This is the old "a later click
   *     wins" guarantee, now by equality instead of a flag.
   *  4. Rail click on an UNSAVED rung → `onSelect` DELETES `?rung=` (D18)
   *     → `preselectRungId` is `null` → this effect returns before touching
   *     the selection; the unsaved rung stays selected. `appliedRungParamRef`
   *     deliberately keeps its last value here rather than resetting to
   *     `null`: nothing can re-navigate to that same value without also
   *     wanting it selected (see the residual below).
   *  5. The D3 origin-reselect effect below → sets the selection AND
   *     `?rung=A` → this effect is the same idempotent re-application as
   *     case 2, applied `A`.
   *  6. "Back to editing" (F9, the fix) → a plain navigate to `?rung=A` and
   *     NOTHING else → A ≠ applied B → A is selected here. This effect is
   *     the only thing that can do it: the D3 effect requires a non-null
   *     `decision`, and a pending sidebar awaiting a decision has none.
   *
   * Residuals, stated rather than fixed:
   *
   *  - An UNSAVED origin rung has `rungId === null`, so `originUrl` carries
   *    no `?rung=` and a return cannot reselect it this way. Rungs persist
   *    write-through on creation, so that state is transient.
   *  - After case 4 the applied ref holds a value the URL no longer carries.
   *    Re-arriving at that exact value is not reachable today: rail writes
   *    use `replace: true` (no history entry to go Back to), a card click
   *    opens a NEW TAB (round 8), and `isOriginUnderneath`'s
   *    `matchesOriginRung` treats an ABSENT `?rung=` as underneath, so
   *    "Back to editing" is not even offered. If that ever tightens, the fix
   *    is to reset `appliedRungParamRef.current = null` on a null param.
   *
   * `appliedRungParamRef` resets at the TOP of the mount effect, not only in
   * cleanup — StrictMode-safe per
   * `docs/v4-system/strictmode-resilient-effects.md`: a cleanup-only reset
   * would leave it stuck from StrictMode's dev double-invoke
   * (`setup→cleanup→setup`) before this effect ever got a chance to apply
   * the preselect on the "real" mount.
   */
  const appliedRungParamRef = useRef<string | null>(null);
  useEffect(() => {
    appliedRungParamRef.current = null;
  }, []);
  useEffect(() => {
    if (!preselectRungId) return;
    if (appliedRungParamRef.current === preselectRungId) return;
    const match = railRungs.find((r) => r.id === preselectRungId);
    if (!match) return;
    setSelectedDraftKey(match.draftKey);
    appliedRungParamRef.current = preselectRungId;
  }, [preselectRungId, railRungs]);

  /** D3/D2 — this play's own subscription to the usage-sidebar store, read by both the rung-switch effect and the decision hook below. */
  const pendingUsageSidebar = useUsageSidebarStore(selectUsageSidebar);

  /**
   * D3 — "Decision must reach the origin even after a rail click." The
   * usage-sidebar decision hook below only fires for the CURRENTLY selected
   * rung's origin key; if the rep clicked to a DIFFERENT rung after opening
   * the sidebar (`saveWithGate`), the pending decision's origin no longer
   * matches. This effect notices that mismatch and selects the origin rung
   * BEFORE the decision can be consumed, on the NEXT render.
   *
   * QA round 8 Codex fixes, F9 — this used to also reset
   * `userClickedRungRef` so a programmatic switch would not latch out the
   * `?rung=` preselect above. That flag is gone; the `?rung=` write below is
   * now all this effect needs, and the preselect effect re-applies it
   * idempotently (interleaving 5 in its doc).
   *
   * The `?rung=` sync mirrors the rail's own `onSelect` handler (below) —
   * without it, `usage-sidebar-logic.ts`'s `isOriginUnderneath` (which reads
   * the REAL route, not this component's local state) would disagree with
   * what is actually rendered.
   *
   * D17 (Codex NEW-RUNG-DELETED-WHILE-PENDING) — before selecting, this
   * verifies `pendingUsageSidebar.origin.rungDraftKey` still exists in
   * `railRungs`. The rep may have navigated to Qualify and removed the rung
   * this pending request was opened for while the sidebar sat open; without
   * this check, `setSelectedDraftKey` below would set a draftKey with no
   * match in `railRungs`, `selectRung`'s fallback chain would silently land
   * on some OTHER rung, and the hoisted decision hook (keyed off THAT other
   * rung's origin) would never match `pendingUsageSidebar.origin` at all —
   * the request would sit `status: 'running'` forever with no hook ever
   * consuming its decision. Marking it `originMissing` instead resets it to
   * `'idle'` with nothing left to select, and `UsageSidebar.tsx` swaps in
   * the removal notice.
   *
   * D18 (Codex NEW-UNSAVED-RUNG-URL-DIVERGENCE) — mirrors the rail
   * `onSelect` handler's `else params.delete('rung')` exactly: an unsaved
   * origin (`rungId === null`) must CLEAR `?rung=`, not merely skip setting
   * it — leaving a stale saved rung id in the URL after reselecting an
   * unsaved one is exactly what let `?rung=` and the actual selection
   * diverge.
   */
  useEffect(() => {
    if (pendingUsageSidebar === null || pendingUsageSidebar.decision === null) return;
    // Hoisted into a local: TS does not carry a discriminant narrowing on
    // `pendingUsageSidebar.origin` into the `railRungs.some` callback below.
    const origin = pendingUsageSidebar.origin;
    // terra-qyt.14 (U9) — `'library'` is the only origin this reselect does
    // NOT apply to; see the identical note on the copy-draft restore effect
    // above.
    if (origin.kind === 'library') return;
    if (origin.playbookId !== resolvedPlaybookId) return;
    if (origin.rungDraftKey === selectedRung?.draftKey) return;

    const originExists = railRungs.some((r) => r.draftKey === origin.rungDraftKey);
    if (!originExists) {
      markUsageSidebarOriginMissing(pendingUsageSidebar.requestId);
      return;
    }

    setSelectedDraftKey(origin.rungDraftKey);
    const params = new URLSearchParams(searchParams);
    if (origin.rungId !== null) {
      params.set('rung', origin.rungId);
    } else {
      params.delete('rung');
    }
    setSearchParams(params, { replace: true });
  }, [pendingUsageSidebar, resolvedPlaybookId, selectedRung?.draftKey, railRungs, searchParams, setSearchParams]);

  /**
   * D3 — hoisted out of the per-rung `SequenceEditorShell` (which used to
   * mount this once per rung and lose it on every rail click/unmount) to
   * here, the play level. `originKey` follows the CURRENTLY selected rung
   * — the effect above is what guarantees selection has already caught up
   * to a pending decision's origin BEFORE this key can match it, on the
   * render after the switch. `''` (never a real origin key, which always
   * starts `play:`/`library:`) is the safe placeholder for "no rung
   * selected yet" — the hook's own effect simply never matches it.
   */
  // terra-qyt.14 (U9) — the `'playbook'` variant when `playId` is undefined
  // (playbook mode), mirroring `SequenceEditorShell`'s own origin
  // construction above.
  const selectedRungOrigin: UsageSidebarOrigin | null =
    selectedRung && resolvedPlaybookId
      ? playId !== undefined
        ? {
            kind: 'play',
            playId,
            campaignSfdcId,
            playbookId: resolvedPlaybookId,
            rungId: selectedRung.id ?? null,
            rungDraftKey: selectedRung.draftKey,
          }
        : {
            kind: 'playbook',
            playbookId: resolvedPlaybookId,
            seedCampaignSfdcId: campaignSfdcId === '' ? null : campaignSfdcId,
            rungId: selectedRung.id ?? null,
            rungDraftKey: selectedRung.draftKey,
          }
      : null;

  /**
   * D3 — the "Update all N" decision's execution. Reads everything it needs
   * off `pending` itself (`origin.rungDraftKey`, `target.id`) rather than a
   * closure over whichever rung happens to be rendered — the whole point of
   * the hoist is that this must still run correctly for rung A's save even
   * after the rep has clicked to rung B (the effect above only guarantees
   * SELECTION catches up; this function must not assume it's acting on
   * "the" rung, only on `pending.origin`'s rung).
   *
   * The acknowledged count sent to the server is `pending.places.length` —
   * NOT the `acknowledgedCount` field frozen at `openUsageSidebar` time —
   * because a `shared_mapping_conflict` retry (`use-usage-sidebar-decision.ts`)
   * updates `pending.places` (via `refreshUsageSidebarUsage`) to the FRESH
   * count without touching `acknowledgedCount`.
   *
   * Plan 2026-09-28-006 U4 (rulebook R9) — inside a play this is option ③,
   * "Update plays and the playbook", reached ONLY after the Save menu's
   * confirm naming the playbook(s) resolved (`SaveMenu`, U7). A Library
   * target is written exactly like the playbook doorway's own "Update all"
   * (no `playId` — the server refuses a play-scoped write to a Library id).
   * An OWNED target never gets ③ from `saveMenuOptions`; if the decision
   * still arrives for one, it writes the copy in place, never the Library.
   */
  async function handleUsageSidebarUpdateAll(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    // terra-qyt.14 (U9) — `'library'` is the only origin excluded here; a
    // `'play'` and a `'playbook'` origin both carry `rungDraftKey` and
    // resolve identically below.
    if (pending.origin.kind === 'library') return;
    // Playbooks QA round 6, U3 (item 1, H2) — `originKeyOf` matches on the
    // ORIGIN (rungDraftKey/playbookId) alone, never on `target.kind`. A
    // SEGMENTATION pending opened from Qualify shares its origin key with
    // this exact rung whenever it's selected here, so without this guard
    // this handler would consume a segmentation decision and post its
    // (empty) `draftSteps` to `saveSequenceSteps` against a SEQUENCE id that
    // was never the one being saved (H2 — the real bug this round found,
    // distinct from the reported one). `QualifyStep`'s own twin handlers
    // already guard the opposite way (`target.kind === 'segmentation'`).
    if (pending.target.kind !== 'sequence') return;
    // Plan 2026-09-28-006 U4 — a SETTINGS pending (sender, CC/reply-to, flow
    // id, rename) shares this same `target.kind: 'sequence'` origin key;
    // dispatch to its own handler before falling into the steps-save logic
    // below, which would otherwise post `pending.draftSteps ?? []` (empty)
    // onto the sequence.
    if (pending.draftSettings !== undefined) return handleSettingsUpdateAll(pending, isStillCurrent);
    // terra-qyt round-3 fix U24 (W1 residual, Design decision 8) — the throw
    // is intentional: `use-usage-sidebar-decision.ts`'s own `.catch` resets
    // the sidebar to idle and toasts this exact sentence, mirroring
    // `GenerateWithAIDrawer.tsx#handleApply`'s pre-flight refusal. Belt-and-
    // suspenders for whatever slips past `closeUsageSidebarForSeedChange`
    // (`usage-sidebar-store.ts`) — a direct URL re-entry with a different
    // seed, mainly.
    if (generationCampaignMismatch(pending.generationInputs, campaignSfdcId)) {
      throw new Error(CAMPAIGN_MOVED_DETAIL);
    }
    if (isOwnedTarget(pending)) return writeOwnedCopyInPlace(pending, isStillCurrent, () => 'Step saved.');
    const sequenceId = pending.target.id;
    const steps = pending.draftSteps ?? [];
    const response = await saveSequenceSteps(
      apiFetch,
      sequenceId,
      steps,
      pending.headVersionId!,
      pending.places.length,
      // terra-qyt.10 — forwards the "Apply to sequence" provenance through
      // to the shared-save path exactly like the sole-use branch
      // (`saveWithGate`'s own `attemptWithPlaces`) already does; `undefined`
      // for every ordinary hand-edit save that goes through this same gate.
      pending.generationInputs ?? undefined,
      // Round-4 QA fixes, U5 (S1) / Decision 6 — the FRESH unsent-play count
      // recomputed from `pending.places` (which a conflict retry keeps
      // current via `refreshUsageSidebarUsage`), same "never the frozen
      // `acknowledged*` field" rule `pending.places.length` right above
      // already follows.
      distinctUnsentPlays(pending.places),
    );
    // D15 (Codex F-B) — the write above already committed server-side
    // regardless; what's guarded is only the CLIENT-side continuation
    // (cache write, draft clear, toast) that would otherwise land against a
    // request that a later `openUsageSidebar` call has since replaced.
    if (!isStillCurrent()) return;
    applySequenceHeadToCache(queryClient, sequenceId, response);
    for (const step of steps) {
      clearCopyDraftIfUnchanged(copyDraftKey(pending.origin.rungDraftKey, sequenceId, step.position), {
        subject: step.subject ?? '',
        body: step.body ?? '',
      });
    }
    // terra-qyt.10 — the staged "Generate with AI" review this write came
    // from (if any) is now baked into the head that just landed; drop it the
    // same way the sole-use "Apply to sequence" path does
    // (`GenerateWithAIDrawer.tsx#handleApply`), so a rep who reopens Generate
    // for this rung+sequence starts fresh rather than re-seeing an already-
    // applied review.
    clearGeneratedCopy(pending.origin.rungDraftKey, sequenceId);
    // Round-4 QA codex-review fix (batch 1, finding 2) — reopen Steps on
    // THIS rung's shell when this write came from "Apply to sequence" (see
    // `signalStepsReopen`'s own doc); a plain hand-edit save through this
    // same gate (`pending.generationInputs` absent) leaves Steps exactly as
    // it was, matching the sole-use branch's own behavior.
    if (pending.generationInputs) signalStepsReopen(pending.origin.rungDraftKey);
    addToast({ content: 'Step saved.', status: 'success' });
  }

  /**
   * Plan 2026-09-28-006 U4 (rulebook R5, R6) — true when the pending's
   * target is THIS play's own sequence (a copy or a play-created original).
   * `saveMenuOptions` reads the same `target.owningPlayId`, so the host and
   * the menu can never disagree about which write a decision means.
   */
  function isOwnedTarget(pending: PendingUsageSidebar): boolean {
    return pending.origin.kind === 'play' && pending.target.owningPlayId === pending.origin.playId;
  }

  /**
   * Plan 2026-09-28-006 U4 (rulebook R8, Decision 6 of plan 004) — the ONE
   * write both "Update N groups in this play" (②) and a stray "Update all"
   * make on an OWNED target: the copy is written in place through the
   * play-scoped copy endpoint (`mode: 'ensure'` — an owned id is never
   * forked), so every group of this play sharing it changes together and
   * nothing outside the play is touched. A `version_conflict` is rethrown
   * untouched — the decision hook parks the Save panel stale with the
   * server's sentence and the rep's draft (R14, plan 2026-09-28-008 U2);
   * nothing reloads until the rep presses Reload.
   *
   * Plan 2026-09-28-007 U3 (R3/R8/R14) — the request names the origin group
   * (`rungId`) and the groups of this play the rep was shown sharing the
   * copy (`confirmedRungIds`, this play's entry of `confirmedFanOutSet`). The
   * server refuses `version_conflict` if the origin group moved off the copy
   * and `shared_mapping_conflict` if another group joined; `successToast`
   * receives the server's `movedRungIds.length` so the count is what
   * actually changed.
   */
  async function writeOwnedCopyInPlace(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
    successToast: (movedGroupCount: number) => string,
  ): Promise<void> {
    if (pending.origin.kind !== 'play') return;
    const { playId: originPlayId, playbookId: originPlaybookId, rungDraftKey, rungId: originRungId } = pending.origin;
    if (originRungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    const sequenceId = pending.target.id;
    const steps = pending.draftSteps ?? [];
    // The rep's own group is always on the list; a places list missing this
    // play entirely still names it, and the server refuses if more share it.
    const confirmedRungIds = confirmedFanOutSet(pending.places, originRungId).plays.find(
      (entry) => entry.playId === originPlayId,
    )?.rungIds ?? [originRungId];
    const response = await savePlaySequenceCopy(
      apiFetch,
      originPlayId,
      sequenceId,
      steps,
      pending.headVersionId!,
      pending.generationInputs ?? undefined,
      { rungId: originRungId, mode: 'ensure', confirmedRungIds },
    );
    if (response.ladder !== null) {
      await adoptPlayLadderMoves(queryClient, {
        playbookId: originPlaybookId,
        playId: originPlayId,
        kind: 'sequence',
        ladder: response.ladder,
        moves: [],
        drafts: 'carry',
      });
    }
    if (!isStillCurrent()) return;
    applySequenceHeadToCache(queryClient, response.sequenceId, {
      versionId: response.versionId,
      versionNumber: null,
      steps: response.steps,
    });
    for (const step of steps) {
      clearCopyDraftIfUnchanged(copyDraftKey(rungDraftKey, response.sequenceId, step.position), {
        subject: step.subject ?? '',
        body: step.body ?? '',
      });
    }
    clearGeneratedCopy(rungDraftKey, response.sequenceId);
    if (pending.generationInputs) signalStepsReopen(rungDraftKey);
    addToast({ content: successToast(response.movedRungIds.length), status: 'success' });
  }

  /**
   * Plan 2026-09-28-006 U4 (rulebook R8) — option ② of the in-play Save
   * menu, for every kind of sequence edit (steps, AI Apply, Load steps,
   * sender, CC/reply-to, flow id, rename):
   *
   *  - OWNED target ("Update N groups in this play") → the copy is written in
   *    place ({@link writeOwnedCopyInPlace}; a settings edit via
   *    `executeSettingsUpdateAll` with `{ playId }`).
   *  - LIBRARY target ("Update N plays") → `POST /plays/:id/update-plays`:
   *    every unsent play still sharing the Library piece gets its own copy
   *    per group, carrying the change; the Library is never written.
   *    The request names the confirmed set — `confirmedFanOutSet(pending.places,
   *    origin.rungId)`, the FRESH places (a conflict retry refreshes
   *    `pending.places`), the same set the button label counts. The origin play's
   *    rewritten ladder is adopted (`adoptUpdatePlaysResult`, the origin
   *    group last), then the origin group's new copy gets the head/drafts
   *    housekeeping a fork gets.
   *
   * Errors: `shared_mapping_conflict` is rethrown untouched — the decision
   * hook refreshes the list and reopens the menu once. `version_conflict`
   * (the Library piece moved) is rethrown untouched too — the hook parks the
   * Save panel stale with the server's sentence (R14, plan 2026-09-28-008
   * U2); the rep's Reload is what refetches the usage and the ladder.
   */
  async function handleUsageSidebarUpdatePlays(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    // Doorways never offer ② (`saveMenuOptions` gives them two options).
    if (pending.origin.kind !== 'play') return;
    if (pending.target.kind !== 'sequence') return;
    if (generationCampaignMismatch(pending.generationInputs, campaignSfdcId)) {
      throw new Error(CAMPAIGN_MOVED_DETAIL);
    }
    const origin = pending.origin;
    if (isOwnedTarget(pending)) {
      if (pending.draftSettings !== undefined) {
        await executeSettingsUpdateAll(apiFetch, queryClient, pending, isStillCurrent, { playId: origin.playId });
        return;
      }
      return writeOwnedCopyInPlace(
        pending,
        isStillCurrent,
        (movedGroupCount) => `Updated ${pluralize(movedGroupCount, 'group')} in this play.`,
      );
    }
    if (origin.rungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    const steps = pending.draftSteps ?? [];
    const change: PlaybooksUpdatePlaysRequest['change'] =
      pending.draftSettings !== undefined
        ? pending.draftSettings
        : {
            field: 'steps',
            steps,
            ...(pending.generationInputs ? { generationInputs: pending.generationInputs } : {}),
          };
    const originRungId = origin.rungId;
    const result = await updatePlays(apiFetch, origin.playId, {
      kind: 'sequence',
      identityId: pending.target.id,
      ...(pending.headVersionId !== null ? { expectedSourceVersionId: pending.headVersionId } : {}),
      confirmed: confirmedFanOutSet(pending.places, originRungId),
      change,
    });
    // The fan-out is real whether or not this request is still current —
    // the origin play's new ladder is adopted unconditionally.
    await adoptUpdatePlaysResult(queryClient, {
      playbookId: origin.playbookId,
      playId: origin.playId,
      kind: 'sequence',
      fromId: pending.target.id,
      result,
    });
    if (!isStillCurrent()) return;
    const originEntry = result.updatedPlays.find((entry) => entry.playId === origin.playId);
    const moved = originEntry?.rungs.find((entry) => entry.rungId === origin.rungId);
    if (moved !== undefined) {
      if (pending.draftSettings !== undefined) {
        applySettingsToCache(queryClient, moved.copyId, pending.draftSettings);
      } else {
        // Cache what the copies were WRITTEN with (tokens normalized,
        // descriptors re-attached), not what was posted.
        applySequenceHeadToCache(queryClient, moved.copyId, {
          versionId: moved.headVersionId,
          versionNumber: null,
          steps: result.steps ?? steps,
        });
        for (const step of steps) {
          clearCopyDraftIfUnchanged(copyDraftKey(origin.rungDraftKey, moved.copyId, step.position), {
            subject: step.subject ?? '',
            body: step.body ?? '',
          });
        }
        clearGeneratedCopy(origin.rungDraftKey, moved.copyId);
      }
    }
    if (pending.generationInputs) signalStepsReopen(origin.rungDraftKey);
    const plays = result.updatedPlays.length;
    addToast({
      content:
        plays > 1
          ? `Updated ${pluralize(plays, 'play')}.`
          : `Updated ${pluralize(originEntry?.rungs.length ?? 1, 'group')} in this play.`,
      status: 'success',
    });
  }

  /**
   * D3 — the "Save a new version" decision's execution: duplicates the
   * shared Sequence (writes the edit onto the DUPLICATE's own head only)
   * and remaps ONLY the origin rung onto it — the play-level equivalent of
   * the old per-rung `handleSaveAsNewCopy`, now reading the rung/playbook
   * to remap off `pending.origin` instead of a closure. It never throws
   * `shared_mapping_conflict` (it has no shared-write of its own to
   * conflict on).
   *
   * U20 (M1, Codex round-2 "Might Break" #1) — it CAN now throw a 409
   * `version_conflict`: `pending.headVersionId` is forwarded as
   * `duplicateSequenceWithEditedSteps`'s `expectedSourceVersionId`, so the
   * server refuses the fork if the SOURCE sequence's head moved after this
   * pending request's steps were generated/edited against it (e.g. a second
   * tab rebound the source's Gong flow and saved its own steps in the
   * meantime — the exact scenario "Update all" already guards via its own
   * `expectedVersionId`, which this handler lacked before). On that
   * conflict this handler invalidates the sequence detail cache (so the
   * editor pane refetches the fresh head) and RETHROWS rather than
   * swallowing it — the hook's own `.catch` (`use-usage-sidebar-decision.ts`)
   * then resets the sidebar to `'idle'` and toasts the server's detail
   * sentence, identical to how "Update all"'s own `version_conflict`
   * surfaces. The rung is never remapped in this case.
   */
  async function handleUsageSidebarSaveNew(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    // terra-qyt.14 (U9) — same exclusion as `handleUsageSidebarUpdateAll`
    // above: only `'library'` is a genuinely different surface.
    if (pending.origin.kind === 'library') return;
    // Playbooks QA round 6, U3 (item 1, H2) — same guard as
    // `handleUsageSidebarUpdateAll` above; see that handler's comment for
    // the full rationale.
    if (pending.target.kind !== 'sequence') return;
    // Plan 2026-09-28-006 U4 — dispatch a settings pending to its own
    // fork-with-remap handler before the steps-fork logic below.
    if (pending.draftSettings !== undefined) return handleSettingsSaveNew(pending, isStillCurrent);
    // Plan 2026-09-27-006 U7a — a play's "Save as new" is the copy-on-write
    // endpoint itself (it forks AND rewrites the origin rung's ladder in one
    // round trip), never the playbook doorway's duplicate + remap below.
    if (pending.origin.kind === 'play') {
      return handlePlayUsageSidebarSaveNew(pending, isStillCurrent);
    }
    // terra-qyt round-3 fix U24 (W1 residual, Design decision 8) — same
    // pre-flight refusal as `handleUsageSidebarUpdateAll` above; see that
    // handler's comment for the full rationale.
    if (generationCampaignMismatch(pending.generationInputs, campaignSfdcId)) {
      throw new Error(CAMPAIGN_MOVED_DETAIL);
    }
    const { playbookId: originPlaybookId, rungDraftKey } = pending.origin;
    const steps = pending.draftSteps ?? [];
    // A `version_conflict` is rethrown untouched: the hook parks the Save
    // panel stale and only the rep's Reload refetches (R14, plan
    // 2026-09-28-008 U2).
    const { newSequenceId } = await duplicateSequenceWithEditedSteps(
      apiFetch,
      pending.target.id,
      steps,
      pending.generationInputs ?? undefined,
      pending.headVersionId ?? undefined,
    );
    // D15 (Codex F-B) — the duplicate write above is real either way; only
    // the client-side continuations below (draft clear, library invalidate)
    // are guarded against a request a later `openUsageSidebar` has replaced.
    if (isStillCurrent()) {
      // B6 (review #9) — the OLD sequence's copy draft at this rung is stale
      // the instant the duplicate exists (its edit is now baked into the
      // duplicate's own head instead), same shape as `handleUsageSidebarUpdateAll`'s
      // own clear: a no-op for any position whose live draft doesn't exactly
      // equal what was just dispatched, so this never clobbers typing the rep
      // did in the meantime.
      for (const step of steps) {
        clearCopyDraftIfUnchanged(copyDraftKey(rungDraftKey, pending.target.id, step.position), {
          subject: step.subject ?? '',
          body: step.body ?? '',
        });
      }
      // terra-qyt.10 — mirrors `handleUsageSidebarUpdateAll`'s own clear: the
      // approved copy is already baked into the duplicate's head by
      // `duplicateSequenceWithEditedSteps` above, so the staged review on
      // the OLD sequence (if any) is stale the same way the hand-typed draft
      // is (B6's own doc, immediately above).
      clearGeneratedCopy(rungDraftKey, pending.target.id);
      void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
    }
    try {
      const response = await remapPlaybookDraftRungSequence(
        apiFetch,
        originPlaybookId,
        rungDraftKey,
        newSequenceId,
      );
      if (!isStillCurrent()) return;
      if (response) void applyLadderSaveToCache(queryClient, originPlaybookId, response);
      const remapped = response
        ? response.rungs.some((r) => r.mapping?.sequenceId === newSequenceId)
        : draftRungMapsToSequence(originPlaybookId, rungDraftKey, newSequenceId);
      if (remapped) {
        // Round-4 QA codex-review fix (batch 1, finding 2) — mirrors
        // `handleUsageSidebarUpdateAll`'s own reopen signal: only fires when
        // this write was AI-apply-originated AND the rung actually landed on
        // the new sequence (the shell's `mapping.sequenceId` reset effect
        // only fires once THAT happens, so signaling before `remapped` is
        // known would target a rung that's about to reset to the OLD
        // sequence's editor, not the new one).
        if (pending.generationInputs) signalStepsReopen(rungDraftKey);
        addToast({ content: 'Saved a new version. This group now uses it.', status: 'success' });
      } else {
        addErrorToast({
          content:
            "The new version was saved, but this group couldn't be switched to it. Pick it under Change sequence, or try again.",
        });
      }
    } catch {
      if (!isStillCurrent()) return;
      addErrorToast({
        content:
          "The new version was saved, but this group couldn't be switched to it. Pick it under Change sequence, or try again.",
      });
    }
  }

  /**
   * Plan 2026-09-27-006 U7a — the play-mode half of "Save as new" (dispatched
   * from {@link handleUsageSidebarSaveNew} above): the copy-on-write endpoint
   * IS the fork-and-remap in one request, so there is no separate duplicate
   * + remap the way the playbook doorway needs — `savePlaySequenceCopy`
   * already rewrites the origin rung's ladder onto the new copy, and its
   * response's `ladder` is adopted the same way `savePlayCopy` adopts a
   * fresh fork.
   *
   * Plan 2026-09-28-006 U4 (rulebook R3) — option ①, "Save as new — this
   * group only": `mode: 'fork'` with `rungId` = the ORIGIN group, so the
   * server makes a fresh copy for that one group (a copy of a copy when the
   * target is already this play's own) and every other group keeps what it
   * points at. A group with no saved id yet (`rungId === null`) cannot own a
   * copy: one plain sentence, no request ([R20]).
   *
   * CAS token: `pending.headVersionId` — the head the sidebar's pending
   * request was recorded against, NOT this shell's own `renderedVersion`
   * (which may by now be rendering a different rung entirely — see
   * `handleUsageSidebarSaveNew`'s own doc on reading everything off
   * `pending`).
   */
  async function handlePlayUsageSidebarSaveNew(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.origin.kind !== 'play') return;
    const { playId: originPlayId, playbookId: originPlaybookId, rungDraftKey, rungId } = pending.origin;
    if (rungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    const steps = pending.draftSteps ?? [];
    // Plan 2026-09-28-008 U2 (R7, R14; Codex round-3 F7) — a failure is
    // rethrown, never swallowed: the decision hook is the ONE place that
    // settles it (a 409 parks the pending stale with the server's sentence;
    // anything else resets it to idle and toasts once). Swallowing it here
    // used to report success to the dispatcher, which closed the pending
    // and lost the rep's steps.
    const response = await savePlaySequenceCopy(
      apiFetch,
      originPlayId,
      pending.target.id,
      steps,
      // A steps pending always carries a real head (only a settings pending
      // carries `null` — this handler is reached only for steps saves; a
      // settings pending dispatches to `handleSettingsSaveNew` first).
      pending.headVersionId!,
      pending.generationInputs ?? undefined,
      // Plan 2026-09-29-001 U1 (R3/R5) — a fresh fork is used by exactly one
      // group (this one); the set it was checked against is `[rungId]`.
      { rungId, mode: 'fork', confirmedRungIds: [rungId] },
    );
    // Plan 2026-09-27-007 U4 — the fork is real whether or not this request
    // is still current, so it is adopted unconditionally (ladder, library
    // invalidation, drafts/reviews rekeyed onto the copy) BEFORE the
    // just-saved drafts are cleared — under the COPY's key, where the rekey
    // just moved them.
    await adoptPlayLadderMoves(queryClient, {
      playbookId: originPlaybookId,
      playId: originPlayId,
      kind: 'sequence',
      ladder: response.ladder,
      moves: [{ rungId, fromId: response.forkedFrom ?? pending.target.id, toId: response.sequenceId }],
      drafts: 'carry',
    });
    applySequenceHeadToCache(queryClient, response.sequenceId, {
      versionId: response.versionId,
      versionNumber: null,
      steps: response.steps,
    });
    for (const step of steps) {
      clearCopyDraftIfUnchanged(copyDraftKey(rungDraftKey, response.sequenceId, step.position), {
        subject: step.subject ?? '',
        body: step.body ?? '',
      });
    }
    clearGeneratedCopy(rungDraftKey, response.sequenceId);
    if (!isStillCurrent()) return;
    if (pending.generationInputs) signalStepsReopen(rungDraftKey);
    addToast({ content: 'Saved a copy for this play. This group now uses it.', status: 'success' });
  }

  /**
   * Plan 2026-09-28-006 U4 (rulebook R6, R9) — "Update all" for a SETTINGS
   * pending (sender, CC/reply-to, flow id, rename): a plain PATCH write via
   * the shared `executeSettingsUpdateAll`. A Library target is the ③ write
   * (no `playId`); an owned target is written in place with `{ playId }`.
   */
  function handleSettingsUpdateAll(pending: PendingUsageSidebar, isStillCurrent: () => boolean): Promise<void> {
    if (isOwnedTarget(pending) && pending.origin.kind === 'play') {
      return executeSettingsUpdateAll(apiFetch, queryClient, pending, isStillCurrent, {
        playId: pending.origin.playId,
      });
    }
    return executeSettingsUpdateAll(apiFetch, queryClient, pending, isStillCurrent);
  }

  /**
   * Playbooks QA round 6, U7 (item 3), widened by plan 2026-09-28-006 U4 to
   * every settings field — "Save as a new sequence" from a RUNG origin. In
   * the playbook doorway: {@link forkSequenceWithSettings}'s shared fork
   * half, then remap ONLY the origin rung onto it. In a play:
   * {@link handlePlaySettingsSaveNew}. No `expectedSourceVersionId`/version-
   * conflict handling: a settings pending carries `headVersionId: null`
   * (round-6 plan decision 10) — there is no compare-and-set on a settings
   * fork.
   *
   * ── IT TOUCHES NO CLIENT DRAFT STATE, DELIBERATELY ────────────────────────
   * Spencer's decision after round 6's third Codex pass: **a sender fork
   * transfers only what is SAVED.** The fork is a server-side copy of the
   * source's saved head content, so saved copy arrives by construction —
   * and this handler does not hand over, merge, or clear any client-side
   * draft. A rep's unsaved copy (and any staged "Generate with AI" review)
   * stays on the ORIGINAL sequence under its own `${rungDraftKey}|${oldId}|…`
   * keys: unreachable while this rung points at the fork, destroyed by
   * nothing, and recoverable if the rung is ever mapped back.
   *
   * This is the ABSENCE of code, so it needs a reason recorded: the handoff
   * that used to live here (`migrateCopyDraftSequence`) produced a data-loss
   * bug in each of three consecutive adversarial review rounds — a destroyed
   * source entry, a stale `RichHtmlField` DOM island, and a stale-baseline
   * misclassification. Deleting it removes the premise of that whole class.
   * Do not re-add a draft handoff here thinking it was an oversight; see
   * `docs/playbooks-usage-sidebar-spec.md`'s "Sender saves (round 6)".
   *
   * `clearGeneratedCopy` went with it, for a reason specific to THIS fork
   * (not a blanket copy of the rule above): its sibling
   * `handleUsageSidebarSaveNew` clears the staged review because
   * `duplicateSequenceWithEditedSteps` has just BAKED that approved copy
   * into the duplicate's head, making the original's review genuinely stale.
   * A settings fork bakes in nothing — it copies the saved head untouched —
   * so clearing here destroyed an unsaved review the rep never discarded,
   * which is exactly what this contract forbids. `clearGeneratedCopy`'s own
   * doc scopes it to "REP-INITIATED actions on the entry currently rendered
   * on screen"; a settings change is neither.
   */
  async function handleSettingsSaveNew(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.origin.kind === 'library' || pending.draftSettings === undefined) return;
    // Plan 2026-09-27-006 U7a — a play's "Save as new sequence" for a
    // settings edit is a per-group fork + a direct write on the new copy:
    // the fork response already rewrites the origin rung's ladder, so there
    // is no separate duplicate + remap the way the playbook doorway needs.
    if (pending.origin.kind === 'play') {
      return handlePlaySettingsSaveNew(pending, isStillCurrent);
    }
    const { playbookId: originPlaybookId, rungDraftKey } = pending.origin;
    const created = await forkSequenceWithSettings(apiFetch, queryClient, pending);
    if (!isStillCurrent()) return;
    try {
      const response = await remapPlaybookDraftRungSequence(apiFetch, originPlaybookId, rungDraftKey, created.id);
      if (!isStillCurrent()) return;
      if (response) void applyLadderSaveToCache(queryClient, originPlaybookId, response);
      const remapped = response
        ? response.rungs.some((r) => r.mapping?.sequenceId === created.id)
        : draftRungMapsToSequence(originPlaybookId, rungDraftKey, created.id);
      if (remapped) {
        addToast({
          content:
            pending.draftSettings.field === 'sender'
              ? 'Saved a new sequence with this sender. This group now uses it.'
              : 'Saved a new sequence with this change. This group now uses it.',
          status: 'success',
        });
      } else {
        addErrorToast({
          content:
            "The new sequence was saved, but this group couldn't be switched to it. Pick it under Change sequence, or try again.",
        });
      }
    } catch {
      if (!isStillCurrent()) return;
      addErrorToast({
        content:
          "The new sequence was saved, but this group couldn't be switched to it. Pick it under Change sequence, or try again.",
      });
    }
  }

  /**
   * Plan 2026-09-28-006 U4 (rulebook R3, R7) — the play-mode half of "Save
   * as new — this group only" from a settings pending (dispatched from
   * {@link handleSettingsSaveNew}): `ensurePlayOwned({ mode: 'fork', rungId })`
   * makes a fresh copy for the ORIGIN group only (a copy of the copy when
   * the target is already this play's own) and rewrites that group's ladder
   * onto it; then the field write lands on the new copy with `{ playId }`.
   * A group with no saved id yet gets one plain sentence and no request.
   */
  async function handlePlaySettingsSaveNew(
    pending: PendingUsageSidebar,
    isStillCurrent: () => boolean,
  ): Promise<void> {
    if (pending.origin.kind !== 'play' || pending.draftSettings === undefined) return;
    const { playId: originPlayId, playbookId: originPlaybookId, rungId } = pending.origin;
    if (rungId === null) {
      addErrorToast({ content: SAVE_GROUP_FIRST });
      return;
    }
    const { ownedId } = await ensurePlayOwned(apiFetch, queryClient, {
      playId: originPlayId,
      playbookId: originPlaybookId,
      kind: 'sequence',
      identityId: pending.target.id,
      rungId,
      mode: 'fork',
    });
    // Plan 2026-09-28-007 U6 (rulebook R7, Codex c1-3) — the fork is real
    // now, so the pending follows it BEFORE the write: the copy, with this
    // group alone. If the write below fails, the error propagates (no catch
    // here) and the decision hook resets this same request to idle with the
    // server's sentence — the sidebar stays open, the edit is kept, and the
    // menu's one "Save" (`saveMenuOptions`, owned + one group) writes onto the
    // copy in place (`handleUsageSidebarUpdatePlays`). A retry never forks a
    // second copy.
    const originPlace = pending.places.find((place) => place.rungId === rungId);
    // This function lives in `SequencesStep`, not the per-rung
    // `SequenceEditorShell` — `railRungs`/`libraryQuery.data` (this step's
    // own state) are what's in scope here, not that shell's `rung`/`library`
    // props.
    const originRung = railRungs.find((r) => r.id === rungId);
    // [R5] The fresh usage read this pending's places came from is always
    // fetched BEFORE this fork (this group's own place should always be
    // among them per `inPlayUsageGate`'s own invariant) — `fallbackRetargetedPlace`
    // is the defensive fallback for a race that loses it anyway; see that
    // function's own doc for the `places: []` bug it replaces.
    const copyPlaces: PlaybooksUsagePlace[] = [
      originPlace
        ? { ...originPlace, sequenceId: ownedId, plays: originPlace.plays.filter((play) => play.id === originPlayId) }
        : fallbackRetargetedPlace({
            playbookId: originPlaybookId,
            playbookName:
              libraryQuery.data?.playbooks.find((playbook) => playbook.id === originPlaybookId)?.name ?? 'This playbook',
            rungId,
            // `originRung` is THIS play's own ladder row for `rungId` — the
            // very origin this pending was gated from, so it is always
            // present; the same invariant-backed `!` this file already uses
            // for `mapping!`/`renderedVersion!`.
            segmentationId: originRung!.segmentationId,
            segmentationName: segmentationNameFor(originRung!),
            sequenceId: ownedId,
            sequenceName: pending.target.name,
            playId: originPlayId,
            campaignSfdcId,
          }),
    ];
    retargetUsageSidebarPending(pending.requestId, { id: ownedId, owningPlayId: originPlayId, places: copyPlaces });
    // Plan 2026-09-27-007 U4 — no `isStillCurrent()` gate on the write: the
    // fork is real either way; the write must follow it, or the rep is left
    // with a copy that never got the change they chose.
    // `executeSettingsUpdateAll` gates only its own cache patch and toast.
    await executeSettingsUpdateAll(
      apiFetch,
      queryClient,
      // Plan 2026-09-28-008 U2 — the copy's places (this group alone), so
      // the write's confirmed groups name only this group: the fork was for
      // it alone, whatever the Library piece's places were.
      { ...pending, target: { ...pending.target, id: ownedId, owningPlayId: originPlayId }, places: copyPlaces },
      isStillCurrent,
      { playId: originPlayId },
    );
  }

  /**
   * Codex fixes 2026-09-17, F2 — two gates on WHEN this play's hook may take
   * a decision, both `''` (matches nothing) until satisfied:
   *
   *  - `flowSettled` — `PlayFlowShell`'s context-change clear is a PARENT
   *    effect and runs AFTER this child's effects in the same commit. On the
   *    way back from a "Go to this play" excursion, a take in that first
   *    commit would restore the review and open the session, and the
   *    shell's `clearGeneratedCopyReviews()` + `closeGenerateSession()`
   *    would then wipe both. `shouldClearFlowStores(last, this)` is the
   *    shell's own predicate; its negation is "the shell has recorded this
   *    flow (or never recorded any — a standalone mount), so nothing is
   *    about to be cleared". `recordFlowSession` runs right after the clear,
   *    the store update re-renders this component, and the key flips from
   *    `''` to real — `use-usage-sidebar-decision.ts` re-runs on `originKey`.
   *  - `originBelongsToFlow` — only for `'resume-review'`: a sibling play of
   *    the same playbook shares the origin key and must not resume a review
   *    generated for a different campaign (see that helper's doc). The
   *    navigation `UsageSidebar` issues alongside the decision means this is
   *    belt-and-suspenders in practice.
   *
   * QA round 8, U7 — the round-7 `'resume-edit'` handler that ALSO read
   * `thisFlowKey` (after its head fetch, via `resumeEditFlowMoved`) is gone
   * with the decision itself; `thisFlowKey` remains because `flowSettled`
   * below is what gates every decision this host consumes.
   */
  const lastFlowContextKey = useFlowSessionStore((state) => state.lastContextKey);
  const thisFlowKey =
    playId !== undefined
      ? flowContextKey({ kind: 'play', playId, campaignSfdcId })
      : resolvedPlaybookId
        ? flowContextKey({ kind: 'playbook', playbookId: resolvedPlaybookId, seedCampaignSfdcId: null })
        : null;
  const flowSettled = thisFlowKey !== null && !shouldClearFlowStores(lastFlowContextKey, thisFlowKey);
  // QA round 8, U7 — narrowed back to `'resume-review'`: the round-7
  // `'resume-edit'` decision is gone (a card opens a new tab, so no excursion
  // can happen from the sidebar).
  const resumeBelongsElsewhere =
    pendingUsageSidebar?.decision === 'resume-review' &&
    !originBelongsToFlow(pendingUsageSidebar.origin, { playId, playbookId: resolvedPlaybookId });

  const usageSidebarHandlers: UsageSidebarDecisionHandlers = {
    updateAll: handleUsageSidebarUpdateAll,
    saveNew: handleUsageSidebarSaveNew,
    // Plan 2026-09-28-006 U4 (rulebook R8) — option ② of the Save menu.
    updatePlays: handleUsageSidebarUpdatePlays,
    // Codex fixes 2026-09-17, F2–F4 — see `usage-sidebar-resume.ts`. The D3
    // effect above has already re-selected the origin rung by the time this
    // hook's key matches (`selectedRungOrigin` follows the selection), which
    // is what fixes F4: a manual rail click no longer strands the resume on
    // the wrong rung, because the effect re-selects the origin (and re-syncs
    // `?rung=`) as soon as the decision lands.
    resumeReview: async (pending) => {
      resumeReviewFromPending(pending);
    },
  };
  useUsageSidebarDecision(
    flowSettled && !resumeBelongsElsewhere && selectedRungOrigin ? originKeyOf(selectedRungOrigin) : '',
    usageSidebarHandlers,
  );

  /** [R20] Never the raw id — "Segment unavailable" when the row is absent (plan 2026-09-28-006 U4). */
  function segmentationNameFor(rung: DraftRung): string {
    return (
      libraryQuery.data?.segmentations.find((s) => s.id === rung.segmentationId)?.name ?? 'Segment unavailable'
    );
  }

  /**
   * U6 (R3) — the sample contact for the selected rung's token-gap warnings
   * and sample-preview card: the same ladder query + id-keyed lookup
   * `MembersPanel` uses to scope its table (`useMatchedMembersLadder` +
   * `matchedIdsForRungScope`, sharing those query keys/cache entries rather
   * than issuing a second fetch), falling back to the plain roster's first
   * row (`selectSampleMember`) per the plan's deferred-question default.
   * Unlike `MembersPanel`, an unresolved/unmatched scope here is NOT an
   * error state to render — a preview always wants a best-effort sample
   * contact, never a "couldn't match" band.
   */
  const { members } = useLauncherMembers(campaignSfdcId);
  /**
   * U10 fix (roster token-gap false-positive) — the real campaign, so the
   * roster-wide token-gap check and the sample preview below can resolve
   * campaign-scoped tokens (`{{Event Name}}`/`{{Event Date}}`/
   * `{{Campaign Type}}`) against real data instead of a hardcoded `null`
   * that made those tokens false-positive as "missing" for every member,
   * every time. Same hook `QualifyStep`'s `ContextCard` already uses for this
   * campaign; `campaign` is `null` while the query is loading, same as
   * `QualifyStep` tolerates.
   */
  const { campaign } = useLauncherCampaign(campaignSfdcId);
  const ladderQuery = useMatchedMembersLadder(campaignSfdcId, resolvedPlaybookId ?? null, playId);
  // Only a PERSISTED rung has a ladder entry — the ladder describes saved state
  // (`rung-counts.ts`), so an unsaved rung resolves to no scope and the preview
  // falls back to the roster's first row rather than showing nothing.
  const matchedIds =
    selectedRung?.id !== undefined
      ? matchedIdsForRungScope(
          ladderQuery.data,
          selectedRung.id,
          playbookQuery.data?.head?.versionId ?? null,
        )
      : null;
  const sampleMember = selectSampleMember(members, matchedIds);
  /**
   * U10 (item 19, R13) — the roster `SequenceEditorShell`'s token-gap check
   * runs against: the same matched-id lookup `sampleMember` above uses, but
   * kept as the REAL matched subset rather than collapsed to one row — and,
   * per the plan's explicit fallback, the WHOLE roster when `matchedIds` is
   * `null` (no ladder entry — an unsaved rung), mirroring
   * `members-panel-scope.ts#membersPanelState`'s `matched` branch for a real
   * (possibly empty) `matchedIds` array.
   */
  const rosterMembers = useMemo(
    () => rosterMembersForGapCheck(members, matchedIds),
    [members, matchedIds],
  );

  /**
   * C3 — the Sequences step's own rung-scope setter, so toggling Preview (or
   * picking a different rung while previewing) filters the shared
   * `MembersPanel` down to that rung's real first-match-wins membership. Until
   * C3 only Qualify's "Preview matches" ever set this scope, which is why the
   * table on this step always showed the whole roster.
   *
   * An UNSAVED rung clears the scope instead of setting one: the ladder
   * describes saved state (`rung-counts.ts`), so an unsaved rung has no
   * matched-member entry to filter by, and scoping to it would make the panel
   * render its "couldn't match" band under this rung's name — a worse answer
   * than the honest full roster.
   */
  const editorMode = useSequencePreviewStore(selectSequenceEditorMode);
  function scopeToRung(rung: DraftRung | null) {
    if (!rung || rung.id === undefined || !resolvedPlaybookId) {
      clearRungScope();
      return;
    }
    setRungScope({
      kind: 'rung',
      rungId: rung.id,
      rungName: segmentationNameFor(rung),
      playbookId: resolvedPlaybookId,
    });
  }

  // terra-qyt.14 (U9) — the play-mode-only guards (`playsQuery`'s
  // loading/error/not-found) never apply in playbook mode: `playbookId` is
  // always the real identity there, `playsQuery` never runs, and `play` is
  // always `null` by construction — none of that means anything is wrong.
  if (playbookId === undefined) {
    if (playsQuery.isLoading) {
      return (
        <div className="flex justify-center p-6">
          <LoadingSpinner size="md" />
        </div>
      );
    }

    if (playsQuery.isError) {
      return (
        <div className="p-6">
          <Alert status="error">Couldn&apos;t load this play. Please try again.</Alert>
        </div>
      );
    }

    if (!play) {
      return (
        <div className="p-6">
          <Text size="sm" variant="tertiary">
            This Play wasn&apos;t found for this campaign — open it again from the campaign home page.
          </Text>
        </div>
      );
    }
  }

  if (playbookQuery.isLoading) {
    return (
      <div className="flex justify-center p-6">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (playbookQuery.isError) {
    return (
      <div className="p-6">
        <Alert status="error">Couldn&apos;t load this Play&apos;s Playbook. Please try again.</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_1fr] lg:items-start">
        <div className="flex flex-col gap-2">
          {railRungs.length === 0 && (
            <Text size="sm" variant="tertiary">
              Add a segmentation on the Qualify step to start.
            </Text>
          )}
          {railRungs.map((rung, i) => {
            const sequence = rung.mapping
              ? libraryQuery.data?.sequences.find((s) => s.id === rung.mapping!.sequenceId)
              : undefined;
            return (
              <SegmentationRailCard
                key={rung.draftKey}
                mapping={rung.mapping}
                index={i}
                segmentationName={segmentationNameFor(rung)}
                sequenceName={sequence?.name ?? null}
                sequenceArchived={sequence !== undefined && isArchivedInLibrary(sequence)}
                isUnsaved={rung.id === undefined}
                library={libraryQuery.data}
                // Highlight follows the DERIVED `selectedRung` (prototype
                // `:6311`, `selectedRung().id === r.id`), not the raw
                // `selectedDraftKey` — on a fresh load the fallback chain
                // auto-selects a rung with no explicit `selectedDraftKey`
                // set yet, and the rail card for that rung must still show
                // as selected to match the editor pane it's paired with.
                draftScope={rung.draftKey}
                isSelected={selectedRung?.draftKey === rung.draftKey}
                onSelect={() => {
                  // QA round 8 Codex fixes, F9 — no "a click latches out
                  // the param" flag any more: the `?rung=` write below IS
                  // how a click wins, and the preselect effect above then
                  // re-applies the same value idempotently (interleaving 2
                  // in its doc).
                  setSelectedDraftKey(rung.draftKey);
                  // D3 — keep `?rung=` in sync with the local selection so
                  // it never diverges from what's actually rendered
                  // (`usage-sidebar-logic.ts`'s `isOriginUnderneath` reads
                  // the real route). Cleared, never set to a stale id, for
                  // an unsaved rung — there is no server-side rung to link
                  // to yet.
                  const params = new URLSearchParams(searchParams);
                  if (rung.id !== undefined) {
                    params.set('rung', rung.id);
                  } else {
                    params.delete('rung');
                  }
                  setSearchParams(params, { replace: true });
                  // C3 — while previewing, the table follows the rail: picking
                  // another rung re-scopes it, so the rows never keep claiming
                  // to be the previously-previewed rung's members.
                  if (editorMode === 'preview') scopeToRung(rung);
                }}
              />
            );
          })}
        </div>

        <div className="flex flex-col gap-3">
          {/*
            C3 — the shared members table lives in the MAIN column, directly
            above the sequence editor whose copy output it drives (plan C3 item
            2), not in the left rail where PR B's slot placement put it. A row
            click sets the preview member; Preview mode filters the rows to this
            rung.
          */}
          {membersPanel}
          {!selectedRung && (
            <div className="rounded-lg border border-border-primary bg-bg-primary p-4">
              <Text size="sm" variant="tertiary">
                Select a segment to edit its sequence.
              </Text>
            </div>
          )}
          {selectedRung && resolvedPlaybookId && (
            <SequenceEditorShell
              key={selectedRung.draftKey}
              playId={playId}
              playbookId={resolvedPlaybookId}
              rung={selectedRung}
              rungName={segmentationNameFor(selectedRung)}
              library={libraryQuery.data}
              sampleMember={sampleMember}
              rosterMembers={rosterMembers}
              campaignMembers={members}
              campaign={campaign}
              campaignSfdcId={campaignSfdcId}
              onScopeToRung={() => scopeToRung(selectedRung)}
            />
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="primary" size="sm" onClick={onGoToReview}>
          Continue to review
          <Icon type={ArrowRight} />
        </Button>
      </div>
    </div>
  );
}
